import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
  ensureBibleHydrated,
  ensureBookAvailable,
  getPrevChapter,
  getNextChapter,
  CANONICAL_SLUG_TO_NAME,
  CANONICAL_SLUG_TO_BOOK_TITLE,
  type BsbBlock,
  type BsbVerse,
  type ParagraphMarker,
} from '@/lib/bible/active';
import { getLastRead, getRecentRead, setLastRead } from '@/lib/bible-progress-store';
import { fetchBookContext, fetchChapterContext } from '@/lib/scripture';
import type {
  ScriptureBookContext,
  ScriptureChapterContext,
} from '@/types/scripture';
import BibleContextBlock from '@/components/BibleContextBlock';
import {
  setBookContextCollapsed,
  setChapterContextCollapsed,
} from '@/lib/bible-context-persistence';
import {
  GLOSSARY_ENABLED,
  PRAY_MODE_ENABLED,
  BIBLE_BOOKMARKS_ENABLED,
  BIBLE_READER_V2_ENABLED,
} from '@/lib/feature-flags';
import { useStudyModeStore } from '@/lib/study-mode-store';
import { useBookmarkStore } from '@/lib/bookmark-store';
import { bookmarkCountsByVerse } from '@/lib/bookmark-utils';
import { useHighlightStore } from '@/lib/highlight-store';
import { highlightsByVerse, highlightId, coerceFamily } from '@/lib/highlight-utils';
import type { BibleHighlight, HighlightFamily } from '@/types/highlight';
import {
  loadHeadwords,
  loadManifest,
  loadOccurrencesForBook,
  type GlossaryEntry,
  type PerBookOccurrences,
} from '@/lib/glossary';
import GlossarySheet from '@/components/GlossarySheet';
import { projectChapterGlossary, type ProjectedGlossarySegment } from '@/lib/glossary-projection';
import { buildScriptureAnchor } from '@/lib/scripture-anchor';
import PrayOnScriptureSheet, {
  type PrayScriptureSheetHandle,
} from '@/components/PrayOnScriptureSheet/PrayOnScriptureSheet';
import BibleBookmarkSheet, {
  type BibleBookmarkSheetHandle,
} from '@/components/BibleBookmarkSheet/BibleBookmarkSheet';
import { useNavLayer, useNavStack } from '@/hooks/useNavLayer';
import { useNoticeStackClearance, useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import { useSettingsStore } from '@/lib/settings-store';
import { bibleErrorFields, reportBibleEvent } from '@/lib/bible-telemetry';
import BibleThemePicker from '@/components/BibleThemePicker/BibleThemePicker';
import BibleFontSizePicker from '@/components/BibleFontSizePicker/BibleFontSizePicker';
import VersePanelSheet from '@/components/VersePanelSheet/VersePanelSheet';
import type { ScriptureAnchor } from '@/types/prayer';
import type { BibleBookmark } from '@/types/bookmark';
import { useReadingAccess, getReadingAccessSnapshot, useDefinitelyOffline } from '@/lib/reading-access';
import { AuthorizedReaderSlot } from '@/components/ReaderRuntime';
import BibleReadonlyNotes from '@/components/BibleReadonlyNotes';
import { getOfflineReopenSnapshot, waitForOfflineReadingSnapshot } from '@/lib/offline-reopen';
import { isCoreReadOnly } from '@/lib/core-cutover';
import styles from './BibleChapter.module.css';

const EMPTY_BOOKMARKS: BibleBookmark[] = [];
const EMPTY_HIGHLIGHTS: BibleHighlight[] = [];

type ReadState =
  | { kind: 'pending' }
  | { kind: 'ready'; blocks: BsbBlock[]; key: string }
  // `offline` distinguishes a definitely-offline failure (honest "connect to
  // download" copy) from a generic failure. Only the load .catch can be
  // offline; synchronous failures are not.
  | { kind: 'failed'; offline: boolean };

// A chapter-load failure is called "offline" ONLY when the device is
// definitely offline (navigator.onLine === false). The pre-2026-08-25 shape
// also treated the bootstrap's manifest_unavailable_no_cache as offline,
// which told CONNECTED users to "connect once" after any transient request
// failure on a fresh device. navigator.onLine === true is still not trusted
// as proof of connectivity — the generic failure copy says "check your
// connection" to cover the lying-onLine case.
function isDefinitelyOffline(): boolean {
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

const MARKER_CLASS: Record<ParagraphMarker, string> = {
  p: styles.marker_p,
  pmo: styles.marker_pmo,
  pc: styles.marker_pc,
  li1: styles.marker_li1,
  li2: styles.marker_li2,
  d: styles.marker_d,
};

// family -> verse-wash CSS-module class (Chunk C, §5.B4). Module scope: it does
// not change. The base .verseHl shape class is added alongside the wash.
const HL_WASH_CLASS: Record<HighlightFamily, string> = {
  promise: styles.verseHlPromise,
  comfort: styles.verseHlComfort,
  command: styles.verseHlCommand,
  conviction: styles.verseHlConviction,
  praise: styles.verseHlPraise,
  character: styles.verseHlCharacter,
  plain: styles.verseHlPlain,
};

// Strict positive-integer pattern. Prevents '1abc' from parseInt'ing
// to 1, which would silently load Genesis 1 from /bible/genesis/1abc.
const CHAPTER_PATTERN = /^[1-9]\d*$/;

// Post-action journal toast (Scripture Journal UX arc Chunk 2). Pause-on-hover
// register matching PrayPage's undo toast; 5s dwell per the plan.
const JOURNAL_SAVED_TOAST_MS = 5000;

// Deferred-open intent for the two verse-panel actions that open a NavStack
// layer (Write a note, Pray). The panel closes FIRST (its requestClose runs a
// navigate(-1)); the destination layer is opened only after the pop lands, so a
// layer is never pushed in the same tick as the panel's requestClose (the
// double-nav blocker). Share + Add verses open no layer, so they stay direct.
type PendingPanelAction =
  | { kind: 'compose' }
  | { kind: 'thread'; verse: number }
  | { kind: 'pray' };

// Search-landing highlight + scroll-into-view tunables (arc 07.4, R4;
// re-center trigger reworked 2026-06-11). The landed ?v= verse is golded
// (until tapped) and centered in view; while async context loads above it,
// the watch loop re-centers ONLY when the verse's DOCUMENT-space position
// moves (a real layout shift), never because the centering formula disagrees
// with the live scroll — formula-vs-scroll polling oscillated on iOS (the
// visualViewport-height feedback from the address bar plus fractional
// scrollY rounding re-scrolled every frame for the full cap: visible
// flicker). LANDING_OFFSET_PX is the minimum gap the verse keeps below the
// sticky header when it is too tall to center. LAYOUT_CAP_MS bounds ONLY the
// continuous rAF watch loop (battery); a context block resolving later still
// re-centers one-shot via its own effect. LANDING_SHIFT_EPSILON_PX is the
// document-space movement that counts as a real layout shift;
// LANDING_APPLY_EPSILON_PX is the minimum scroll correction worth applying
// (subpixel-rounding immunity). SCROLL_KEYS are the keyboard inputs that
// signal the user has taken over scrolling (so we stop re-centering and
// never fight them); touch-drag and wheel signal the same.
const LANDING_OFFSET_PX = 16;
// Verse-panel device-feedback follow-up: the docked panel's height cap, in
// lockstep with `.panel { height: min(50dvh, 520px) }` in
// VersePanelSheet.module.css. scrollVerseAbovePanel uses it so a tapped verse
// lands in the live reader strip ABOVE the panel, not behind it.
const PANEL_MAX_DVH = 0.5;
const PANEL_MAX_PX = 520;
const LAYOUT_CAP_MS = 3000;
const LANDING_SHIFT_EPSILON_PX = 2;
const LANDING_APPLY_EPSILON_PX = 4;
const SCROLL_KEYS = new Set([
  'PageUp',
  'PageDown',
  'Home',
  'End',
  'ArrowUp',
  'ArrowDown',
  ' ',
  'Spacebar',
]);

// A scroll key whose default action scrolls the document: no handler consumed
// it, and it was not typed into an editable field (where it moves the caret).
function keyScrollsDocument(e: KeyboardEvent): boolean {
  if (!SCROLL_KEYS.has(e.key) || e.defaultPrevented) return false;
  const target = e.target;
  return !(
    target instanceof HTMLElement &&
    (target.isContentEditable || target.closest('input, textarea, select') !== null)
  );
}

export default function BibleChapter() {
  const { t } = useTranslation();
  const reading = useReadingAccess();
  const definitelyOffline = useDefinitelyOffline();
  const readOnly = isCoreReadOnly() || reading.restricted;
  const authoringIsCurrent = useCallback(() => {
    const current = getReadingAccessSnapshot();
    return !readOnly && !current.restricted && current.epoch === reading.epoch && current.ownerId === reading.ownerId;
  }, [readOnly, reading.epoch, reading.ownerId]);
  const { bookSlug, chapter } = useParams<{
    bookSlug: string;
    chapter: string;
  }>();

  const chapterIsStrictInt = !!chapter && CHAPTER_PATTERN.test(chapter);
  const chapterNum = chapterIsStrictInt ? Number.parseInt(chapter, 10) : NaN;
  const bookName = bookSlug ? CANONICAL_SLUG_TO_NAME.get(bookSlug) : undefined;
  const bookTitle = bookSlug
    ? CANONICAL_SLUG_TO_BOOK_TITLE.get(bookSlug)
    : undefined;
  const inputsValid =
    !!bookSlug &&
    !!bookName &&
    chapterIsStrictInt &&
    Number.isInteger(chapterNum) &&
    chapterNum > 0;

  // Verse-jump target (Bible-search L1): a ?v= query param set by the
  // search "Go to" banner and verse-hit links on /bible. Validated with
  // the SAME strict positive-int discipline as CHAPTER_PATTERN (rule #71),
  // so junk like ?v=0 / ?v=01 / ?v=3abc is ignored and falls through to
  // normal scroll restoration. Query param (not router state) so the jump
  // is deep-linkable and survives reload.
  const [searchParams] = useSearchParams();
  const rawVerseParam = searchParams.get('v');
  const targetVerse =
    rawVerseParam && CHAPTER_PATTERN.test(rawVerseParam)
      ? Number.parseInt(rawVerseParam, 10)
      : null;

  const [state, setState] = useState<ReadState>({ kind: 'pending' });
  // Freeze supplementary chrome only for a real offline/local-reading visit,
  // or after the reader takes over while admission is resolving. A normal
  // online cache hit must not permanently suppress its context and Study UI.
  const contextKey = `${bookSlug}/${chapterNum}`;
  const offlineVisit = definitelyOffline || (reading.restricted && reading.local !== null);
  const [contextVisit, setContextVisit] = useState(() => ({ key: contextKey, restricted: offlineVisit }));
  if (contextVisit.key !== contextKey) {
    setContextVisit({ key: contextKey, restricted: offlineVisit });
  } else if (offlineVisit && !contextVisit.restricted) {
    setContextVisit({ key: contextKey, restricted: true });
  }
  const contextAllowed = !reading.restricted && !definitelyOffline && contextVisit.key === contextKey && !contextVisit.restricted;
  useEffect(() => {
    if (!reading.restricted || contextVisit.restricted) return;
    const moved = () => setContextVisit((current) => current.key === contextKey
      ? { ...current, restricted: true } : current);
    const keyed = (event: KeyboardEvent) => { if (keyScrollsDocument(event)) moved(); };
    window.addEventListener('wheel', moved, { passive: true });
    window.addEventListener('touchmove', moved, { passive: true });
    window.addEventListener('keydown', keyed);
    return () => {
      window.removeEventListener('wheel', moved);
      window.removeEventListener('touchmove', moved);
      window.removeEventListener('keydown', keyed);
    };
  }, [reading.restricted, contextVisit.restricted, contextKey]);
  const mountedRef = useRef(true);

  // Search-landing gold highlight (arc 07.4, R4). The landed ?v= verse renders
  // its full text in gold (the Study-Mode glow register) so the user instantly
  // sees the verse they searched. It is NOT derived from stored state: rendering
  // off the LIVE `targetVerse` (the current URL's ?v=) means a same-mount param
  // change to a ?v-less route shows nothing, with no stale cross-chapter target
  // to re-match. The gold PERSISTS through scrolling and clears ONLY when the
  // user taps the verse (or presses Escape); `dismissed` is per-jump and resets
  // whenever the jump identity changes (effect below), so a fresh navigation
  // re-shows the gold.
  const [dismissed, setDismissed] = useState(false);
  const dismissLanding = useCallback(() => setDismissed(true), []);

  // Lookup-vs-reading session flag (reading-history arc). A ?v= arrival is a
  // LOOKUP: its checkpoints pass lookup:true to setLastRead so the visit lands
  // in the recents history WITHOUT claiming the "Continue reading" slot — a
  // quick verse lookup must never steal continue-reading from the chapter the
  // user was actually deep-reading. The session PROMOTES to a normal read on
  // dwell (60 s) or the first real scroll input (wheel / touch / scroll keys —
  // user-input events only, so programmatic verse-centering never promotes).
  const lookupRef = useRef(false);
  // "Back to {book} {chapter}" return chip: shown on a lookup landing when the
  // last deep read is a DIFFERENT chapter, so the user can look up a verse and
  // hop straight back without visiting the index. Captured once per jump
  // identity; dismissible; still useful after promotion so it stays until
  // dismissed or navigation.
  const returnAccessKey = `${reading.ownerId ?? ''}:${bookSlug}/${chapterNum}?v=${targetVerse ?? ''}`;
  const [returnTarget, setReturnTarget] = useState<{
    accessKey: string;
    bookSlug: string;
    bookName: string;
    chapter: number;
  } | null>(null);
  const activeReturnTarget = returnTarget?.accessKey === returnAccessKey && (!reading.restricted || reading.local) ? returnTarget : null;
  const [returnDismissed, setReturnDismissed] = useState(false);
  // Shared by the two layout-stabilization effects (initial/continuous and
  // context-transition re-scroll): `takenOverRef` latches once the user actively
  // scrolls (touch/wheel/keys) so we never fight them while re-centering.
  const takenOverRef = useRef(false);

  // Study Mode (Bible reader tap-to-define). Feature-flagged; flag-off
  // means the whole branch is dead code and pays zero bundle cost (no
  // network calls, no tokenize call, no GlossarySheet mount).
  //
  // Three lazy fetches drive the runtime data:
  //   - manifest:    pre-fetched on mount when flag is on. The Study
  //                  Mode row is hidden until this resolves
  //                  successfully — manifest-gated fail-closed behavior
  //                  so a deployment with the flag flipped but no data
  //                  files present silently degrades to "no Study Mode"
  //                  rather than showing a broken toggle.
  //   - headwords:   ~80 KB gzipped global map (definitions). Fetched
  //                  the first time the user turns Study Mode on.
  //   - occurrences: per-book per-verse allowed-set. Fetched when the
  //                  user is in Study Mode and visits a curated book.
  //                  Stored alongside the bookSlug it was loaded for
  //                  so a route-change race can't render stale glow.
  type LoadedOccurrences = { slug: string; map: PerBookOccurrences };
  const studyMode = useStudyModeStore((s) => s.enabled);
  const toggleStudyMode = useStudyModeStore((s) => s.toggle);
  const setStudyEnabled = useStudyModeStore((s) => s.setEnabled);
  // Reader theme (Bible reader rethink, Chunk A). Reactive read; applied to the
  // reader .container via data-bible-theme below. Defaults to 'night', so the
  // reading surface is byte-identical to prod until the operator flips to paper.
  const bibleTheme = useSettingsStore((s) => s.bibleTheme);
  // Chunk D: one-time verse-gesture coachmark. Read reactively so the banner
  // (in the sticky group below) and the openVerse mark-seen both see live state.
  const verseCoachmarkSeen = useSettingsStore((s) => s.verseCoachmarkSeen);
  const markVerseCoachmarkSeen = useSettingsStore((s) => s.markVerseCoachmarkSeen);
  // Phase 3 reader markers subscribe to the live bookmark store (tombstones
  // already filtered in loadBookmarks). Called unconditionally per the hooks
  // rule; the store is empty in a flag-off build (main.tsx skips hydration),
  // so this is a harmless empty-array subscription there.
  const liveBookmarks = useBookmarkStore((s) => s.bookmarks);
  // Admission clears the live projections before rehydrating. Keep the same-
  // owner snapshot until that load settles, so annotations never blink away.
  const [bookmarksLoadedEpoch, setBookmarksLoadedEpoch] = useState<number | null>(null);
  const localForOwner = reading.local?.ownerId === reading.ownerId ? reading.local : null;
  const bookmarks = reading.restricted || (localForOwner && bookmarksLoadedEpoch !== reading.epoch)
    ? (localForOwner?.bookmarks ?? EMPTY_BOOKMARKS) : liveBookmarks;
  // Reader highlight wash (Chunk C). Reactive read of the live store; the store
  // is empty in a flag-off build (main.tsx skips hydration under v2), so this is
  // a harmless empty-array subscription there.
  const liveHighlights = useHighlightStore((s) => s.highlights);
  const [highlightsLoadedEpoch, setHighlightsLoadedEpoch] = useState<number | null>(null);
  const highlights = reading.restricted || (localForOwner && highlightsLoadedEpoch !== reading.epoch)
    ? (localForOwner?.highlights ?? EMPTY_HIGHLIGHTS) : liveHighlights;
  const [headwords, setHeadwords] = useState<Map<string, GlossaryEntry> | null>(
    null,
  );
  const [manifestBooks, setManifestBooks] = useState<Set<string> | null>(null);
  const [occurrences, setOccurrences] = useState<LoadedOccurrences | null>(null);
  // Global definition failures and per-book artifact failures retry independently.
  const [headwordsError, setHeadwordsError] = useState(false);
  const [occurrencesError, setOccurrencesError] = useState<string | null>(null);
  const currentGlossaryError = headwordsError || occurrencesError === bookSlug;
  const [activeEntry, setActiveEntry] = useState<GlossaryEntry | null>(null);

  // Mode-indicator hint banners (Study / Pray / Bookmark) are NOT state:
  // they render persistently inside the sticky header whenever their mode is
  // active (driven by studyMode / prayMode / bookmarkMode + the flag gates),
  // and ride along on scroll. No showHint flag, no auto-dismiss, no
  // tap-to-dismiss. See the render block inside .stickyGroup.

  // Pray Mode (Bible reader: select verses or a whole chapter and pray
  // on them). Non-persisted and per-chapter (locked decision #3): it
  // defaults off, turns on for the current chapter view, and resets on
  // chapter change. Mutually exclusive with Study Mode (the two toggle
  // handlers coordinate this locally). Flag-gated by PRAY_MODE_ENABLED;
  // flag-off keeps the whole branch dead.
  const [prayMode, setPrayMode] = useState(false);
  const [selectedVerses, setSelectedVerses] = useState<Set<number>>(
    () => new Set(),
  );
  // The capture sheet mounts when this is non-null. BibleChapter builds the
  // anchor at handler time (it owns blocks + selection); the sheet receives
  // the finished anchor, so the empty / all-omitted case is a clean no-op
  // here and never opens an empty sheet.
  const [prayingAnchor, setPrayingAnchor] = useState<ScriptureAnchor | null>(
    null,
  );

  // Bookmark Mode (Bible reader: select one or more verses and save them as
  // a bookmark with an optional note; whole-chapter bookmarking was cut in
  // the thread redesign). Mirrors Pray Mode's selection mechanic:
  // non-persisted, per-chapter, mutually exclusive with Study + Pray, and
  // sharing the one `selectedVerses` set. A SAVED verse is a tappable thread
  // (the OPEN branch in renderVerse), not a selection target. Flag-gated by
  // BIBLE_BOOKMARKS_ENABLED; flag-off keeps the whole branch dead.
  const [bookmarkMode, setBookmarkMode] = useState(false);
  // The COMPOSE sheet mounts when this is non-null (selection -> Bookmark
  // create flow). Anchor is built at handler time from blocks + selection,
  // so an empty / all-omitted selection is a clean no-op (never opens an
  // empty sheet). Renamed from bookmarkingAnchor now that a second entry
  // path (the verse-tap thread) also opens the sheet.
  const [composeAnchor, setComposeAnchor] =
    useState<ScriptureAnchor | null>(null);
  // The THREAD sheet mounts when this is non-null: the verse number tapped
  // on a saved verse run. Its single-verse anchor is derived in threadAnchor
  // below. Only one of composeAnchor / threadVerse is ever set.
  const [threadVerse, setThreadVerse] = useState<number | null>(null);
  const [intentEpoch, setIntentEpoch] = useState(reading.epoch);
  // Revoke only transient UI intents. Existing sheet cleanup retains its drafts
  // and recordings; a previous admission's action cannot reopen for a new one.
  if (intentEpoch !== reading.epoch) {
    setIntentEpoch(reading.epoch);
    setPrayingAnchor(null);
    setComposeAnchor(null);
    setThreadVerse(null);
    setPrayMode(false);
    setBookmarkMode(false);
  }

  // --- Bible reader v2 (verse-tap gesture, Chunk B redesign). Gated on
  // BIBLE_READER_V2_ENABLED; flag-off leaves these inert (the v2 renderVerse
  // branch + select-bar never render). REUSES the existing selection machinery
  // (selectedVerses / toggleVerse / handlePrayOnSelected): tapping an UNSAVED
  // verse enters this sub-mode and the slim action bar offers Keep / Pray /
  // Share on the selection, WITHOUT resurrecting the retired Pray/Bookmark mode
  // booleans. Tapping an already-KEPT verse opens its thread (the rich view)
  // instead. ---
  // The anchor verse of the live contiguous selection (verse-panel interaction
  // redesign). Set by openVerse on the first tap; extendSelectionTo grows a
  // contiguous range from it while the panel is open. null when no panel.
  const [anchorVerse, setAnchorVerse] = useState<number | null>(null);
  // Reader-level Share confirm toast (§4.6): shown only on the clipboard
  // fallback (the native Web Share sheet is its own confirmation). Auto-clears
  // on a ~2.5s timer below.
  const [shareToast, setShareToast] = useState(false);
  const [journalToast, setJournalToast] = useState(false);
  const journalToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelJournalToast = useCallback(() => {
    if (journalToastTimerRef.current) {
      clearTimeout(journalToastTimerRef.current);
      journalToastTimerRef.current = null;
    }
  }, []);

  const scheduleJournalToast = useCallback(() => {
    cancelJournalToast();
    journalToastTimerRef.current = setTimeout(() => {
      setJournalToast(false);
      journalToastTimerRef.current = null;
    }, JOURNAL_SAVED_TOAST_MS);
  }, [cancelJournalToast]);

  const showJournalSavedToast = useCallback(() => {
    if (!BIBLE_BOOKMARKS_ENABLED && !BIBLE_READER_V2_ENABLED) return;
    setJournalToast(true);
    scheduleJournalToast();
  }, [scheduleJournalToast]);

  useEffect(() => () => cancelJournalToast(), [cancelJournalToast]);
  // The unified verse panel (verse-panel redesign): opened by tapping any verse,
  // operates on the active selectedVerses set. Holds the color grid + Write a
  // note / Pray / Share / + Add verses. Mounts CLOSED -> StrictMode-safe.
  const [versePanelOpen, setVersePanelOpen] = useState(false);
  const [versePanelFocusOnOpen, setVersePanelFocusOnOpen] = useState(false);
  const versePanelReturnFocusRef = useRef<HTMLButtonElement>(null);
  // Read by the last-read restore without becoming one of its dependencies. A
  // layout effect keeps it in step with the commit that mounts or removes the
  // panel, so no later task can see the panel open while the ref says closed.
  const versePanelOpenRef = useRef(versePanelOpen);
  useLayoutEffect(() => {
    versePanelOpenRef.current = versePanelOpen;
  }, [versePanelOpen]);
  const [highlightBusy, setHighlightBusy] = useState(false);
  const [highlightMutationFailed, setHighlightMutationFailed] = useState(false);
  // NavStack coordinator + deferred-open intent for the two panel actions that
  // open a layer (Note + Pray). See PendingPanelAction at module scope.
  const nav = useNavStack();
  const pendingPanelActionRef = useRef<(PendingPanelAction & { epoch: number }) | null>(null);

  // Reader thread markers (thread-redesign §4.1): per-verse note count for
  // THIS chapter from the live store. bookSlug-guarded so renderVerse can
  // read the Map without touching the optional bookSlug (Revision 3
  // BLOCKER 2). Map.has(v) = "verse v is bookmarked" (drives the tint +
  // which verse runs open a thread); Map.get(v) = note count for the
  // verse-run aria-label. Tombstones are already filtered in loadBookmarks;
  // bookmarkCountsByVerse re-guards !deletedAt as defense in depth. Legacy
  // whole-chapter rows (verses: []) contribute nothing here (D5). Keyed on
  // the anchor's bookSlug/chapter (source of truth), not the denormalized
  // row fields. Short-circuits when the flag is off so a flag-off build does
  // zero derivation work.
  const verseBookmarkCounts = useMemo(() => {
    if (!BIBLE_BOOKMARKS_ENABLED || !bookSlug) return new Map<number, number>();
    return bookmarkCountsByVerse(bookmarks, bookSlug, chapterNum);
  }, [bookmarks, bookSlug, chapterNum]);

  // Reader highlight wash (Chunk C, §5.B3): per-verse live highlight for THIS
  // chapter. Mirrors verseBookmarkCounts (gated on v2, bookSlug-guarded) and
  // short-circuits to an empty map when the flag is off so flag-off does zero
  // derivation work and renderVerseV2 is byte-identical to today.
  const verseHighlights = useMemo(() => {
    if (!BIBLE_READER_V2_ENABLED || !bookSlug)
      return new Map<number, BibleHighlight>();
    return highlightsByVerse(highlights, bookSlug, chapterNum);
  }, [highlights, bookSlug, chapterNum]);

  // Present (existing, non-omitted) verse numbers in this chapter (verse-panel
  // interaction redesign). extendSelectionTo intersects its raw [lo..hi] range
  // with this set so selectedVerses NEVER holds an omitted verse (which would
  // gap the citation AND suppress the active swatch via coerceFamily(undefined)).
  // Mirrors extractChapterVerses' walk (scripture-anchor.ts): only paragraph /
  // poetry blocks carry verses. Flag-gated like verseHighlights so flag-off does
  // zero work.
  const presentVerses = useMemo(() => {
    const s = new Set<number>();
    if (!BIBLE_READER_V2_ENABLED || state.kind !== 'ready') return s;
    for (const block of state.blocks) {
      if (block.type === 'paragraph') {
        for (const v of block.verses) if (!v.omitted) s.add(v.v);
      } else if (block.type === 'poetry') {
        for (const line of block.lines) {
          for (const v of line.verses) if (!v.omitted) s.add(v.v);
        }
      }
    }
    return s;
  }, [state]);

  // Defense-in-depth mount reload (feature plan §5.2). Boot hydration + the
  // sync handlers + Phase 2's reactive writes already keep the store live;
  // this catches an edge where the store was never hydrated this session.
  // Calls a zustand store action (not React setState), so it does not trip
  // react-hooks/set-state-in-effect. The .catch mirrors main.tsx's boot
  // posture (loadBookmarks has try/finally, no catch, so it can reject).
  useEffect(() => {
    if (reading.restricted || getReadingAccessSnapshot().restricted || !BIBLE_BOOKMARKS_ENABLED) return;
    let cancelled = false;
    void useBookmarkStore.getState().loadBookmarks().then(() => {
      const current = getReadingAccessSnapshot();
      if (!cancelled && !current.restricted && current.epoch === reading.epoch && current.ownerId === reading.ownerId) {
        setBookmarksLoadedEpoch(reading.epoch);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [reading.restricted, reading.epoch, reading.ownerId]);

  // Same defense-in-depth mount reload for highlights (Chunk C, §5.B3), gated
  // on v2. Calls a zustand action (not React setState), so no
  // react-hooks/set-state-in-effect concern.
  useEffect(() => {
    if (reading.restricted || getReadingAccessSnapshot().restricted || !BIBLE_READER_V2_ENABLED) return;
    let cancelled = false;
    void useHighlightStore.getState().loadHighlights().then(() => {
      const current = getReadingAccessSnapshot();
      if (!cancelled && !current.restricted && current.epoch === reading.epoch && current.ownerId === reading.ownerId) {
        setHighlightsLoadedEpoch(reading.epoch);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [reading.restricted, reading.epoch, reading.ownerId]);

  // User-action retry: if the previous fetch errored, clear the error
  // synchronously before flipping Study Mode so the next render-cycle's
  // effect re-attempts the load. Keeps the toggle-off cleanup OUT of
  // an effect, avoiding the react-hooks/set-state-in-effect lint flag.
  // The persistent Study banner is render-driven by studyMode, so this
  // handler no longer fires/clears any hint flag.
  const handleToggleStudyMode = useCallback(() => {
    setHeadwordsError(false);
    setOccurrencesError(null);
    const turningOn = !studyMode;
    toggleStudyMode();
    if (turningOn) {
      // Mutual exclusivity: turning Study on turns Pray + Bookmark off and
      // clears the shared verse selection + any open capture/thread sheet
      // (both bookmark sheet states, for symmetry, though the header toggle
      // is unreachable while a sheet overlay is open).
      setPrayMode(false);
      setPrayingAnchor(null);
      setBookmarkMode(false);
      setComposeAnchor(null);
      setThreadVerse(null);
      setSelectedVerses(new Set());
    }
  }, [toggleStudyMode, studyMode]);

  // Pray Mode toggle. Mutually exclusive with Study (forces Study off
  // via the store's setEnabled when turning Pray on). Tapping the active
  // Pray label again clears it back to plain reading and drops the
  // selection. The persistent Pray banner is render-driven by prayMode.
  const handleTogglePray = useCallback(() => {
    if (!authoringIsCurrent()) return;
    const turningOn = !prayMode;
    setPrayMode(turningOn);
    if (turningOn) {
      setStudyEnabled(false);
      // Carryover fix (advisor round 1): the shared selection can now be
      // populated by Bookmark mode, so entering Pray must clear it (and both
      // open bookmark sheets), not only clear on the OFF transition.
      setSelectedVerses(new Set());
      setBookmarkMode(false);
      setComposeAnchor(null);
      setThreadVerse(null);
    } else {
      setSelectedVerses(new Set());
    }
  }, [prayMode, setStudyEnabled, authoringIsCurrent]);

  // Bookmark Mode toggle. Mirrors handleTogglePray: mutually exclusive with
  // Study (setStudyEnabled(false)) and Pray, clears the shared selection +
  // the other mode's open sheet on turn-on, and drops the selection on
  // turn-off. The persistent Bookmark banner is render-driven by bookmarkMode.
  const handleToggleBookmark = useCallback(() => {
    if (!authoringIsCurrent()) return;
    const turningOn = !bookmarkMode;
    setBookmarkMode(turningOn);
    if (turningOn) {
      setStudyEnabled(false);
      setPrayMode(false);
      setPrayingAnchor(null);
      setSelectedVerses(new Set());
    } else {
      setSelectedVerses(new Set());
      setComposeAnchor(null);
    }
  }, [bookmarkMode, setStudyEnabled, authoringIsCurrent]);

  // Highlighter toggle for a single verse.
  const toggleVerse = useCallback((v: number) => {
    setSelectedVerses((prev) => {
      const nextSet = new Set(prev);
      if (nextSet.has(v)) {
        nextSet.delete(v);
      } else {
        nextSet.add(v);
      }
      return nextSet;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedVerses(new Set());
  }, []);

  // Live contiguous-selection extension (verse-panel interaction redesign).
  // Called while the panel is open: grow a CONTIGUOUS passage from anchorVerse
  // to the tapped verse, INTERSECTED with presentVerses so an interior omitted
  // verse is dropped (keeps selectedVerses present-only -> the citation and the
  // active swatch stay correct). Defensive: a tap with no anchor treats v as the
  // anchor (single-verse selection).
  const extendSelectionTo = useCallback(
    (v: number) => {
      const a = anchorVerse ?? v;
      if (anchorVerse == null) setAnchorVerse(v);
      const lo = Math.min(a, v);
      const hi = Math.max(a, v);
      const next = new Set<number>();
      for (let n = lo; n <= hi; n++) if (presentVerses.has(n)) next.add(n);
      setSelectedVerses(next);
    },
    [anchorVerse, presentVerses],
  );

  // Bottom-bar actions build the scripture anchor from the reader's
  // in-memory blocks + the current selection, then open the capture sheet.
  // buildScriptureAnchor returns null on an empty / all-omitted selection,
  // so a degenerate tap is a clean no-op (no empty sheet).
  const handlePrayOnSelected = useCallback(() => {
    if (!authoringIsCurrent()) return;
    if (state.kind !== 'ready' || !bookSlug) return;
    const anchor = buildScriptureAnchor({
      bookSlug,
      chapter: chapterNum,
      blocks: state.blocks,
      selection: { kind: 'verses', verses: [...selectedVerses] },
    });
    if (anchor) setPrayingAnchor(anchor);
  }, [state, bookSlug, chapterNum, selectedVerses, authoringIsCurrent]);
  const handlePrayOnChapter = useCallback(() => {
    if (!authoringIsCurrent()) return;
    if (state.kind !== 'ready' || !bookSlug) return;
    const anchor = buildScriptureAnchor({
      bookSlug,
      chapter: chapterNum,
      blocks: state.blocks,
      selection: { kind: 'chapter' },
    });
    if (anchor) setPrayingAnchor(anchor);
  }, [state, bookSlug, chapterNum, authoringIsCurrent]);

  // Sheet saved a prayer: close it and clear the verse selection. Under the
  // verse-panel redesign the persistent additive bar is gone (the panel closed
  // before Pray opened via the deferred-open handoff), so the selection clears
  // on save under v2 too. v1 always cleared here, so this is unchanged for v1.
  const handlePraySaved = useCallback(() => {
    setPrayingAnchor(null);
    setSelectedVerses(new Set());
  }, []);

  // Selection -> Bookmark COMPOSE action (bottom bar, 1+ verses selected).
  // Whole-chapter bookmarking is CUT (thread-redesign §2), so there is no
  // handleBookmarkChapter. Build the passage anchor from the reader's
  // in-memory blocks + selection; buildScriptureAnchor returns null on an
  // empty / all-omitted selection, so a degenerate tap is a clean no-op.
  const handleBookmarkSelected = useCallback(() => {
    if (!authoringIsCurrent()) return;
    if (state.kind !== 'ready' || !bookSlug) return;
    const anchor = buildScriptureAnchor({
      bookSlug,
      chapter: chapterNum,
      blocks: state.blocks,
      selection: { kind: 'verses', verses: [...selectedVerses] },
    });
    if (anchor) setComposeAnchor(anchor);
  }, [state, bookSlug, chapterNum, selectedVerses, authoringIsCurrent]);

  // COMPOSE sheet saved a bookmark: close it, clear the selection, KEEP
  // Bookmark mode on (the user may bookmark another passage). COMPOSE-ONLY
  // (thread-redesign §4.4, Revision 5 LOW): the THREAD sheet's "Add a note"
  // does NOT close, so it never calls this.
  const handleBookmarkSaved = useCallback(() => {
    setComposeAnchor(null);
    setSelectedVerses(new Set());
    showJournalSavedToast();
  }, [showJournalSavedToast]);

  // Share action (verse-panel redesign). Operates on the current selection (the
  // passage), reusing the same buildScriptureAnchor the Pray path uses, and
  // Web-Shares "reference\ntext" + a watchhim.work line (public scripture only;
  // clipboard fallback shows the reader toast). The retired "Keep" verb and its
  // "Add a thought" sibling are gone: Important/Other is the colorless save and
  // the panel's Write a note / Notes (N) reaches the bookmark thread.
  const handleShareSelected = useCallback(() => {
    if (state.kind !== 'ready' || !bookSlug || selectedVerses.size === 0) return;
    const anchor = buildScriptureAnchor({
      bookSlug,
      chapter: chapterNum,
      blocks: state.blocks,
      selection: { kind: 'verses', verses: [...selectedVerses] },
    });
    if (!anchor) return;
    const payload = `${anchor.reference}\n${anchor.text}\n\n${t('bible.verseShareAttribution')}`;
    const copyFallback = () => {
      void navigator.clipboard
        ?.writeText(payload)
        .then(() => setShareToast(true))
        .catch(() => {});
    };
    if (typeof navigator.share === 'function') {
      void navigator.share({ text: payload }).catch(() => copyFallback());
    } else {
      copyFallback();
    }
  }, [state, bookSlug, chapterNum, selectedVerses, t]);

  // Highlight palette actions (Chunk C; verse-panel interaction redesign §2.3).
  // Apply writes one highlight row per selected verse via a per-verse
  // buildScriptureAnchor (for the frozen reference + to skip omitted verses
  // cleanly). The panel STAYS OPEN and the selection STAYS (a color stacks): the
  // panel's activeFamily + canRemove derive reactively from verseHighlights, so
  // the tapped swatch shows active and "Remove color" appears immediately, and
  // the wash is visible on the page above the non-modal panel. So the user can
  // color, recolor, remove, then also write / pray / share the same verse
  // without re-tapping. Remove tombstones every highlighted verse, same stay-open.
  const handleApplyHighlight = useCallback(
    async (family: HighlightFamily, sublabel: string | null) => {
      if (!authoringIsCurrent() || highlightBusy) return;
      if (state.kind !== 'ready' || !bookSlug || selectedVerses.size === 0) return;
      const items = [];
      for (const v of selectedVerses) {
        const a = buildScriptureAnchor({
          bookSlug,
          chapter: chapterNum,
          blocks: state.blocks,
          selection: { kind: 'verses', verses: [v] },
        });
        if (!a) continue; // omitted verse -> skip
        items.push({
          bookSlug,
          chapter: chapterNum,
          verse: v,
          family,
          sublabel,
          reference: a.reference,
        });
      }
      if (items.length === 0) return;
      setHighlightBusy(true);
      setHighlightMutationFailed(false);
      try {
        await useHighlightStore.getState().setHighlightsBatch(items);
        showJournalSavedToast();
      } catch (err) {
        console.error('[Adia] highlight save failed', err);
        setHighlightMutationFailed(true);
      } finally {
        setHighlightBusy(false);
      }
    },
    [
      state,
      bookSlug,
      chapterNum,
      selectedVerses,
      showJournalSavedToast,
      authoringIsCurrent,
      highlightBusy,
    ],
  );

  const handleRemoveHighlight = useCallback(async () => {
    if (!authoringIsCurrent() || highlightBusy) return;
    const ids = Array.from(selectedVerses)
      .filter((verse) => verseHighlights.has(verse))
      .map((verse) => highlightId(bookSlug ?? '', chapterNum, verse));
    if (ids.length === 0) return;
    setHighlightBusy(true);
    setHighlightMutationFailed(false);
    try {
      await useHighlightStore.getState().removeHighlightsBatch(ids);
    } catch (err) {
      console.error('[Adia] highlight remove failed', err);
      setHighlightMutationFailed(true);
    } finally {
      setHighlightBusy(false);
    }
  }, [
    bookSlug,
    chapterNum,
    selectedVerses,
    verseHighlights,
    authoringIsCurrent,
    highlightBusy,
  ]);

  // Verse-panel action callbacks (verse-panel redesign). Note + Pray open a
  // NavStack layer, so they DEFER: stash the intent, close ONLY the panel, and
  // let the navHasVersePanel watcher fire once the pop lands (no double-nav).
  // Share opens no layer, so it runs directly after the close.
  const handlePanelWriteNote = useCallback(() => {
    if (!authoringIsCurrent()) return;
    // Single verse that already has notes -> the rich thread; otherwise compose
    // a passage note. A legacy empty-note "Keep" row counts as noteCount === 1
    // and opens the thread in its "kept, no thought yet" state.
    const theVerse =
      selectedVerses.size === 1 ? [...selectedVerses][0] : undefined;
    pendingPanelActionRef.current =
      theVerse !== undefined && verseBookmarkCounts.has(theVerse)
        ? { kind: 'thread', verse: theVerse, epoch: reading.epoch }
        : { kind: 'compose', epoch: reading.epoch };
    setVersePanelOpen(false);
  }, [selectedVerses, verseBookmarkCounts, authoringIsCurrent, reading.epoch]);

  const handlePanelPray = useCallback(() => {
    if (!authoringIsCurrent()) return;
    pendingPanelActionRef.current = { kind: 'pray', epoch: reading.epoch };
    setVersePanelOpen(false);
  }, [authoringIsCurrent, reading.epoch]);

  const handlePanelShare = useCallback(() => {
    setVersePanelOpen(false);
    handleShareSelected();
    handleClearSelection();
  }, [handleShareSelected, handleClearSelection]);

  // Single-verse anchor for the verse-tap THREAD sheet (thread-redesign
  // §4.3). Built from the same in-memory blocks the reader holds; null
  // whenever no verse is tapped or the chapter is not ready, so the sheet
  // only mounts on a non-null anchor.
  const threadAnchor = useMemo(() => {
    if (threadVerse == null || state.kind !== 'ready' || !bookSlug) return null;
    return buildScriptureAnchor({
      bookSlug,
      chapter: chapterNum,
      blocks: state.blocks,
      selection: { kind: 'verses', verses: [threadVerse] },
    });
  }, [threadVerse, state, bookSlug, chapterNum]);

  // NavStack layers for the route-world sheets (AB, rule #78). Each `isOpen`
  // mirrors its render gate EXACTLY (incl. the build-time flag, so a flag-off
  // build never registers). All four are false at mount -> StrictMode-safe.
  // glossary has no stateful close -> raw state-clear. The two save-bearing
  // sheets delegate browser-back to their imperative requestClose() (§4a) so
  // back routes through the sheet's own handleBackdrop (with a mid-save defer).
  const prayRef = useRef<PrayScriptureSheetHandle>(null);
  const composeRef = useRef<BibleBookmarkSheetHandle>(null);
  const threadRef = useRef<BibleBookmarkSheetHandle>(null);
  const zGlossary = useNavLayer(
    'glossary',
    GLOSSARY_ENABLED && contextAllowed && activeEntry !== null,
    () => setActiveEntry(null),
  );
  const closePrayScripture = () => {
    // A real route departure must not restore a layer on the departed chapter.
    if (window.location.pathname !== `/bible/${bookSlug}/${chapterNum}`) {
      setPrayingAnchor(null);
      return;
    }
    if (prayRef.current?.requestClose() === 'keep' && !nav.has('pray-scripture')) {
      nav.push('pray-scripture', closePrayScripture);
    }
  };
  const zPray = useNavLayer(
    'pray-scripture',
    !readOnly && PRAY_MODE_ENABLED && prayingAnchor !== null,
    closePrayScripture,
  );
  const zCompose = useNavLayer(
    'bookmark-compose',
    !readOnly && BIBLE_BOOKMARKS_ENABLED && composeAnchor !== null,
    () => composeRef.current?.requestClose(),
  );
  const zThread = useNavLayer(
    'bookmark-thread',
    BIBLE_BOOKMARKS_ENABLED && threadAnchor !== null && (!readOnly || !BIBLE_READER_V2_ENABLED),
    () => readOnly ? setThreadVerse(null) : threadRef.current?.requestClose(),
  );
  // Unified verse panel (verse-panel redesign). No stateful close -> raw
  // state-clear. The two layer-opening panel actions (Note + Pray) use the
  // deferred-open handoff below so a destination layer is never pushed in the
  // same tick as this panel's requestClose.
  const zVersePanel = useNavLayer(
    'verse-panel',
    BIBLE_READER_V2_ENABLED && versePanelOpen,
    () => setVersePanelOpen(false),
  );
  // Deferred-open watcher (verse-panel redesign): when the panel layer is gone
  // (its requestClose pop has landed) and an intent is stashed, fire it now. The
  // selection (selectedVerses) survives the panel close (only the save handlers
  // clear it), so the deferred open targets the right passage. A depth-only
  // navigate(-1) does not change bookSlug/chapterNum, so the per-chapter reset
  // does not fire. Share + Add verses open no layer, so they never stash here.
  const navHasVersePanel = useSyncExternalStore(nav.subscribe, () =>
    nav.has('verse-panel'),
  );
  useEffect(() => {
    if (navHasVersePanel) return; // panel still open
    const pending = pendingPanelActionRef.current;
    if (!pending) return;
    pendingPanelActionRef.current = null;
    if (!authoringIsCurrent() || pending.epoch !== reading.epoch) return;
    // setState-in-effect: the documented NavStack deferred-open deviation
    // (rule #75 / rule #82's retired handoff), fired only when the pop lands.
    if (pending.kind === 'compose') handleBookmarkSelected();
    else if (pending.kind === 'thread') setThreadVerse(pending.verse);
    else handlePrayOnSelected();
  }, [navHasVersePanel, handleBookmarkSelected, handlePrayOnSelected, authoringIsCurrent, reading.epoch]);
  // v2 Share toast auto-dismiss (§4.6). Timer-driven (no synchronous setState
  // in the effect body), mirroring the AutoStopToast register.
  useEffect(() => {
    if (!shareToast) return;
    const timer = setTimeout(() => setShareToast(false), 2500);
    return () => clearTimeout(timer);
  }, [shareToast]);

  // Pray Mode is per-chapter and non-persisted: reset it and the verse
  // selection whenever the route's book or chapter changes so a new chapter
  // always opens in plain reading mode. Navigation-driven setState-in-effect,
  // the same documented deviation as the load + context effects below. (The
  // mode hint banners are render-driven by mode-active, so there is no hint
  // state to reset here.)
  useEffect(() => {
    setPrayMode(false);
    setSelectedVerses(new Set());
    // Close the capture sheet if the route changes while it's open.
    setPrayingAnchor(null);
    // Bookmark mode shares the per-chapter reset. Both sheet entry paths
    // (compose + thread) clear so no stale sheet survives navigation
    // (thread-redesign §4.3, Revision 5 LOW).
    setBookmarkMode(false);
    setComposeAnchor(null);
    setThreadVerse(null);
    // v2 (verse-panel interaction redesign): no passage selection survives
    // navigation; close the verse panel and clear its anchor. Additive; the v1
    // clears above stay for flag-off.
    setAnchorVerse(null);
    setVersePanelOpen(false);
  }, [bookSlug, chapterNum]);

  // Manifest pre-fetch on mount when the flag is on. Failure stays
  // silent (one console.warn per failed fetch attempt; loadManifest's
  // module cache clears on failure so subsequent BibleChapter mounts
  // retry naturally). manifestBooks stays null on failure → the row
  // stays hidden → fail-closed.
  useEffect(() => {
    if (!GLOSSARY_ENABLED || !contextAllowed || getReadingAccessSnapshot().restricted) return;
    if (manifestBooks) return;
    let cancelled = false;
    loadManifest()
      .then((mf) => {
        if (cancelled) return;
        setManifestBooks(mf.books);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[glossary] manifest fetch failed (Study Mode hidden):', err);
      });
    return () => {
      cancelled = true;
    };
  }, [manifestBooks, contextAllowed]);

  // Study-mode glossary offline warmup (Chunk 3 §4.B). Mirrors the
  // BibleBookIndex bible-search warmup: ask the service worker to precache the
  // glossary corpus (manifest + headwords + every curated book's occurrence
  // file) into its CURRENT CACHE_NAME, so a curated book never opened in Study
  // mode online still glows offline. An SW-context write is deterministic where
  // a page fetch is not (it uses the bumped cache name, which survives update
  // deploys, and does not depend on page control or the loaders' module promise
  // cache). Gated on Study mode so only Study users pay the growing corpus cost
  // (rule #66); the studyMode dep re-fires the moment the user toggles Study on.
  // The in-memory loaders (loadManifest / loadHeadwords / loadOccurrencesForBook)
  // stay as-is; this SW handler is the durable, deploy-surviving layer beside
  // them. The page sends only the bare trigger, never URLs.
  useEffect(() => {
    if (!GLOSSARY_ENABLED || !studyMode || !contextAllowed || getReadingAccessSnapshot().restricted) return;
    if (!('serviceWorker' in navigator)) return;
    const trigger = (sw: ServiceWorker | null) => {
      if (getReadingAccessSnapshot().restricted) return;
      sw?.postMessage({ type: 'precache-glossary' });
    };
    navigator.serviceWorker.ready
      .then((reg) => trigger(reg.active))
      .catch(() => {});
    const onControllerChange = () =>
      trigger(navigator.serviceWorker.controller);
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
    };
  }, [studyMode, contextAllowed]);

  // Lazy-load headwords the first time Study Mode is enabled.
  // Module-scoped cache inside glossary.ts means re-renders don't refetch.
  useEffect(() => {
    if (!GLOSSARY_ENABLED || !contextAllowed || getReadingAccessSnapshot().restricted) return;
    if (!studyMode) return;
    if (headwordsError) return;
    if (headwords) return;
    let cancelled = false;
    loadHeadwords()
      .then((hw) => {
        if (cancelled) return;
        setHeadwords(hw);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[glossary] headwords load failed:', err);
        setHeadwordsError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [studyMode, headwords, headwordsError, contextAllowed]);

  // Lazy-load per-book occurrences when Study Mode + book change.
  // Skipped entirely if the book isn't in the manifest's curated set.
  // The projection's render-time stale guard handles the brief
  // window where this effect hasn't yet replaced prior-book occurrences,
  // so no synchronous bookSlug-clear effect is needed (fewer effect-time
  // setState calls = fewer lint suppressions).
  useEffect(() => {
    if (!GLOSSARY_ENABLED || !contextAllowed || getReadingAccessSnapshot().restricted) return;
    if (!studyMode) return;
    if (!bookSlug) return;
    if (!manifestBooks) return;
    if (!manifestBooks.has(bookSlug)) return; // uncurated; render guard yields null
    if (occurrencesError === bookSlug) return;
    let cancelled = false;
    loadOccurrencesForBook(bookSlug)
      .then((m) => {
        if (cancelled) return;
        setOccurrences({ slug: bookSlug, map: m });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[glossary] occurrences load failed:', err);
        setOccurrencesError(bookSlug);
      });
    return () => {
      cancelled = true;
    };
  }, [studyMode, bookSlug, manifestBooks, occurrencesError, contextAllowed]);

  const navigate = useNavigate();
  const chapterHeadingRef = useRef<HTMLHeadingElement>(null);
  const stickyGroupRef = useRef<HTMLDivElement>(null);
  // App notice stack: an in-flow spacer above the sticky header keeps the
  // chapter chrome (prev/next, the Define coachmark) and the first verses
  // below the fixed notices at the top of the chapter. While a notice shows,
  // the sticky group sticks below the stack (an inline `top` at the stack's
  // clearance minus the safe-area inset its negative margin cancels, the same
  // edge the spacer reserves, so the header sits at one position at rest and
  // stuck) instead of at top 0, so prev/next and the coachmark stay reachable
  // once the reader scrolls. Rule #74's solid background, no compression, no
  // backdrop filter and no transition are unchanged; with no notice the CSS
  // `top: 0` applies as before.
  //
  // Reading band (v0.2.0 follow-through 2): the header sticks below the
  // notices only while the band it leaves above the bottom of the screen holds
  // at least 2.5 lines of the reader's text (measured live, so it follows the
  // text size). Otherwise (measured: 375x559 with Extra Large text and both
  // notices, where the stuck header would end 13 px below the screen) it is not
  // sticky while that holds: it scrolls away with the page and the verses below
  // the notices can be read and tapped; at the top of the chapter it still sits
  // below the notices through the spacer, so prev/next stay reachable there.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();
  const noticeClearance = useNoticeStackClearance();
  const noticeClearanceRef = useRef(noticeClearance);
  useEffect(() => {
    noticeClearanceRef.current = noticeClearance;
  }, [noticeClearance]);
  const STUCK_READING_BAND_LINES = 2.5;
  const [stickBelowNotices, setStickBelowNotices] = useState(true);
  useLayoutEffect(() => {
    const group = stickyGroupRef.current;
    if (noticeClearance <= 0 || !group) return;
    // The reader is absent while a chapter loads and is remounted with every
    // chapter, so it is looked up at each measurement (never a captured,
    // possibly detached element), and this effect re-runs when the chapter or
    // its loading state changes so the observer follows the mounted reader
    // (v0.2.0 review fix B2).
    const mountedReader = () => group.parentElement?.querySelector<HTMLElement>(`.${styles.reader}`) ?? null;
    const reader = mountedReader();
    let frame = 0;
    const measure = () => {
      frame = 0;
      const current = mountedReader();
      const style = current ? getComputedStyle(current) : null;
      const lineHeight = style
        ? Number.parseFloat(style.lineHeight) || Number.parseFloat(style.fontSize) * 1.7
        : Number.NaN;
      // No reader text to measure (still loading) or an unreadable line
      // height: keep the header sticky, the behaviour before the rule.
      if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
        setStickBelowNotices(true);
        return;
      }
      // The band below the header once stuck at the clearance. Independent of
      // whether it is stuck, so the choice cannot flap.
      const band = window.innerHeight - (noticeClearance + group.getBoundingClientRect().height);
      setStickBelowNotices(band >= STUCK_READING_BAND_LINES * lineHeight);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const sizes = new ResizeObserver(schedule);
    sizes.observe(group);
    if (reader) sizes.observe(reader);
    window.addEventListener('resize', schedule);
    measure();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      sizes.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [noticeClearance, inputsValid, state.kind, bookSlug, chapterNum]);
  // The sticky header's bottom edge once stuck: its sticky top offset (0, or
  // below the notice stack while a notice shows) plus its live height. The
  // verse-landing math and the last-read lookup keep verses below it. When it
  // does not stick (too little reading band below the notices) it adds
  // nothing: the edge is the notices' clearance alone.
  const stuckHeaderBottom = useCallback((): number => {
    const group = stickyGroupRef.current;
    if (!group) return 0;
    const style = getComputedStyle(group);
    if (style.position !== 'sticky') return noticeClearanceRef.current;
    const top = Number.parseFloat(style.top) || 0;
    return top + group.getBoundingClientRect().height;
  }, []);
  // Desired scrollY that lands a target verse just below the sticky header.
  // Returns null when no matching [data-verse] node exists (verse past chapter
  // end, or a vCont-only verse with no first-fragment anchor) so callers can
  // fall back to top. This is the SHARED jump mechanism: the Bible-bookmarks
  // Phase 4 jump-to-verse reuses it rather than forking a second [data-verse]
  // consumer. `v` is a validated positive integer, so the attribute selector
  // needs no escaping.
  const computeVerseTop = useCallback((v: number): number | null => {
    const el = document.querySelector<HTMLElement>(`[data-verse="${v}"]`);
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const headerBottom = stuckHeaderBottom();
    // Use the VISUAL viewport height (excludes the iOS address bar) so the verse
    // lands centered on what the user actually sees. Center the verse box
    // vertically, but never let its top slide under the sticky header (a verse
    // taller than half the viewport falls back to just-below-the-header; the
    // header's stuck bottom includes its offset below any app notice).
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const pad = Math.max(headerBottom + LANDING_OFFSET_PX, vh / 2 - rect.height / 2);
    const top = window.scrollY + rect.top - pad;
    // Rounded so callers (effect A's watch loop AND effect B's one-shot)
    // never chase fractional iOS scroll positions.
    return Math.max(0, Math.round(top));
  }, [stuckHeaderBottom]);

  // Scroll the target verse into view (centered), or to top when not found.
  const scrollVerseIntoView = useCallback(
    (v: number) => {
      const top = computeVerseTop(v);
      window.scrollTo(0, top ?? 0);
    },
    [computeVerseTop],
  );

  // Scroll a tapped verse into the live reader strip ABOVE the docked verse
  // panel (verse-panel follow-up), so opening the panel never hides the verse
  // behind it. Centers the verse within [header bottom .. (vh - panelPx)];
  // falls back to just below the header when that strip is too short, and to
  // top when the verse has no [data-verse] node. Geometry-stable: the panel is
  // position:fixed, so only the end-of-chapter spacer reflows. While an app
  // notice shows, the header's stuck bottom includes its offset below the
  // notices and the panel's height is capped below the stack's clearance
  // (lockstep with VersePanelSheet's inline max-height).
  const scrollVerseAbovePanel = useCallback((v: number) => {
    const el = document.querySelector<HTMLElement>(`[data-verse="${v}"]`);
    if (!el) {
      window.scrollTo(0, 0);
      return;
    }
    const rect = el.getBoundingClientRect();
    const headerBottom = stuckHeaderBottom();
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const clearance = noticeClearanceRef.current;
    const panelPx = Math.min(
      vh * PANEL_MAX_DVH,
      PANEL_MAX_PX,
      clearance > 0 ? Math.max(0, vh - clearance) : Number.POSITIVE_INFINITY,
    );
    const strip = vh - panelPx - headerBottom;
    const pad = Math.max(
      headerBottom + LANDING_OFFSET_PX,
      headerBottom + strip / 2 - rect.height / 2,
    );
    const top = Math.max(0, Math.round(window.scrollY + rect.top - pad));
    window.scrollTo(0, top);
  }, [stuckHeaderBottom]);

  const prev = useMemo(
    () => (bookSlug ? getPrevChapter(bookSlug, chapterNum) : null),
    [bookSlug, chapterNum],
  );
  const next = useMemo(
    () => (bookSlug ? getNextChapter(bookSlug, chapterNum) : null),
    [bookSlug, chapterNum],
  );

  // Active taxonomy locale derived from the user's i18n preference.
  // Defensive (i18n.language ?? 'en').slice(0, 2) mirrors the scripture.ts
  // precedent at line 247. Only en-US is seeded today; es and pt users
  // hit a null result and see the localized empty state.
  const taxonomyLocale = useMemo(() => {
    const lang = (i18n.language ?? 'en').slice(0, 2);
    if (lang === 'es') return 'es-MX';
    if (lang === 'pt') return 'pt-BR';
    return 'en-US';
  }, []);

  const [bookContextRow, setBookContextRow] =
    useState<ScriptureBookContext | null>(null);
  const [bookContextState, setBookContextState] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');

  const [chapterContextRow, setChapterContextRow] =
    useState<ScriptureChapterContext | null>(null);
  const [chapterContextState, setChapterContextState] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle');

  // Book context is fetched + rendered only for locales whose taxonomy
  // is seeded in scripture_book_contexts. en-US is the only seeded
  // locale today; es-MX and pt-BR users skip both the network round-trip
  // and the disclosure render entirely.
  const bookContextSupported = contextAllowed && taxonomyLocale === 'en-US';
  const chapterContextSupported = contextAllowed && taxonomyLocale === 'en-US';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadStartedRestricted = useRef(false);
  const [admissionRetry, setAdmissionRetry] = useState(0);
  const startLoad = useCallback(() => {
    loadStartedRestricted.current = getReadingAccessSnapshot().restricted;
    if (!inputsValid || !bookSlug) {
      setState({ kind: 'failed', offline: false });
      return () => {};
    }
    setState({ kind: 'pending' });
    let cancelled = false;
    // Requested-book-first (2026-08-25): serve THIS book — local IDB
    // first (zero network wait when it's on device), else a direct
    // single-book fetch. The old path awaited the FULL 66-book
    // hydration (plus a live manifest check) before rendering any
    // chapter, so one failed request on a fresh device blocked every
    // chapter, and flaky wifi stalled even a fully-downloaded Bible
    // behind the manifest timeout.
    // Let the coordinator decide whether a fenced snapshot is still pending,
    // including captive Wi-Fi with navigator.onLine true. Resolve it alongside
    // public BSB text so the saved position is known before the chapter paints.
    const snapshotReady = waitForOfflineReadingSnapshot();
    Promise.all([ensureBookAvailable(bookSlug), snapshotReady])
      .then(([book]) => {
        if (cancelled || !mountedRef.current) return;
        const blocks = book.chapters[String(chapterNum)] ?? null;
        if (!blocks) {
          // Book present but the chapter is missing: not an offline failure.
          setState({ kind: 'failed', offline: false });
          return;
        }
        setState({ kind: 'ready', blocks, key: `${bookSlug}/${chapterNum}` });
      })
      .catch((err) => {
        if (cancelled || !mountedRef.current) return;
        console.warn('[bible-chapter] load failed:', err);
        const offline = isDefinitelyOffline();
        // Definitely-offline failures are expected behavior (nothing
        // downloaded yet, no network); only apparent-online failures
        // are reliability signals worth a log line.
        if (!offline && !getReadingAccessSnapshot().restricted) {
          reportBibleEvent('bible_chapter_load_failed', {
            book: bookSlug,
            chapter: chapterNum,
            ...bibleErrorFields(err),
          });
        }
        setState({ kind: 'failed', offline });
      });
    // Background: keep the full-corpus offline promise + sha updates
    // moving. Fire-and-forget — a full-hydration failure must never
    // block the chapter the user asked for; it surfaces on the Bible
    // index's download affordance instead.
    ensureBibleHydrated().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [bookSlug, chapterNum, inputsValid]);

  // The synchronous setState({kind: 'pending'}) inside startLoad is intentional:
  // URL params (bookSlug, chapterNum) drive re-load and we want the pending state
  // to render immediately on navigation. Mirrors BibleBookIndex's intentional
  // hooks-rule deviation pattern.
  useEffect(() => {
    const cancel = startLoad();
    return cancel;
  }, [startLoad, admissionRetry]);

  useEffect(() => {
    if (reading.restricted || state.kind !== 'failed' || !loadStartedRestricted.current) return;
    loadStartedRestricted.current = false;
    // A cache miss before admission may fetch once verification passes.
    setAdmissionRetry((attempt) => attempt + 1);
  }, [reading.restricted, state.kind]);

  useEffect(() => {
    if (!inputsValid || !bookName) return;
    const prior = document.title;
    document.title = t('bible.chapterTitleFormat', {
      book: bookName,
      chapter: chapterNum,
    });
    return () => {
      document.title = prior;
    };
  }, [bookName, chapterNum, inputsValid, t]);

  const restoreVisit = useRef({ key: '', restored: false, moved: false });
  // The spot the reader last chose (or the restore applied), with the page
  // height at that moment. The checkpoint writer compares against it.
  const readerSpotRef = useRef<{ y: number; height: number } | null>(null);
  const markReaderSpot = () => {
    readerSpotRef.current = { y: window.scrollY, height: document.documentElement.scrollHeight };
  };
  useLayoutEffect(() => {
    const key = `${bookSlug}/${chapterNum}?v=${targetVerse ?? ''}`;
    if (restoreVisit.current.key !== key) {
      restoreVisit.current = { key, restored: false, moved: false };
      readerSpotRef.current = null;
    }
    const moved = () => { restoreVisit.current.moved = true; };
    const keydown = (event: KeyboardEvent) => { if (keyScrollsDocument(event)) moved(); };
    window.addEventListener('wheel', moved, { passive: true });
    window.addEventListener('touchmove', moved, { passive: true });
    window.addEventListener('keydown', keydown);
    return () => {
      window.removeEventListener('wheel', moved);
      window.removeEventListener('touchmove', moved);
      window.removeEventListener('keydown', keydown);
    };
  }, [bookSlug, chapterNum, targetVerse]);

  // Heading focus + last-read restoration (arc 07.4 R4: scrolling a ?v target
  // into view is owned by the two layout-stabilization effects below, NOT here).
  // For a ?v jump we only focus the heading for SR orientation (rule #72
  // preventScroll so it does not undo the programmatic scroll). With no ?v we
  // restore the last-read offset (or top), then focus. The offline snapshot is
  // already loaded with the chapter, so restore it in layout before text paint.
  useLayoutEffect(() => {
    if (state.kind !== 'ready' || state.key !== `${bookSlug}/${chapterNum}` || !bookSlug || restoreVisit.current.restored) return;
    if (reading.restricted && !reading.local) {
      // A contended local-owner read may outlive the local-read watchdog.
      // Once public text is painted offline, never jump to a late snapshot.
      if (isDefinitelyOffline() || getOfflineReopenSnapshot().offline) restoreVisit.current.restored = true;
      return;
    }
    if (restoreVisit.current.moved) { restoreVisit.current.restored = true; return; }

    if (targetVerse !== null) {
      restoreVisit.current.restored = true;
      chapterHeadingRef.current?.focus({ preventScroll: true });
      return;
    }

    if (reading.local && reading.local.ownerId === reading.ownerId) {
      const recent = reading.local.recentReads.find((row) => row.bookSlug === bookSlug && row.chapter === chapterNum);
      const last = reading.local.lastRead;
      const offset = recent?.scrollOffset ?? (last?.bookSlug === bookSlug && last.chapter === chapterNum ? last.scrollOffset : 0);
      restoreVisit.current.restored = true;
      window.scrollTo(0, offset);
      markReaderSpot();
      chapterHeadingRef.current?.focus({ preventScroll: true });
      return;
    }

    // The last-read reads are asynchronous (IndexedDB behind the owner check),
    // and the verses are already rendered and usable while they run. If the
    // reader acts in that window, this one-shot restore is stale: scrolling to
    // the saved offset and focusing the heading would take the reader away
    // from what they chose (and send their next key to the heading). It is
    // skipped when focus moved (a keyboard user on a verse number, a verse
    // panel taking focus) or when the verse panel is open (a tapped verse;
    // WebKit does not focus a button on click, so focus alone misses it).
    // It is also skipped when the reader scrolled while the reads were pending:
    // a wheel, a touch drag, or a key that scrolls the document. Only those
    // INPUT events count, never `scroll` events, so the app's own scrolling
    // (window.scrollTo, the verse-above-panel scroll, browser scroll
    // restoration) never does. The listeners exist only while the reads are
    // pending. Focus that has not moved since the restore began (the body, or
    // the chapter link the reader activated), with no panel open and no
    // scrolling input, keeps the rule #72 order unchanged.
    const focusAtStart = document.activeElement;
    let readerScrolled = false;
    const onScrollInput = () => {
      readerScrolled = true;
    };
    const onScrollKey = (e: KeyboardEvent) => {
      if (keyScrollsDocument(e)) readerScrolled = true;
    };
    window.addEventListener('wheel', onScrollInput, { passive: true });
    window.addEventListener('touchmove', onScrollInput, { passive: true });
    window.addEventListener('keydown', onScrollKey, { passive: true });
    const stopScrollInput = () => {
      window.removeEventListener('wheel', onScrollInput);
      window.removeEventListener('touchmove', onScrollInput);
      window.removeEventListener('keydown', onScrollKey);
    };
    const readerTookOver = (): boolean => {
      if (versePanelOpenRef.current || readerScrolled) return true;
      const active = document.activeElement;
      return (
        active !== null &&
        active !== focusAtStart &&
        active !== document.body &&
        active !== chapterHeadingRef.current
      );
    };
    let cancelled = false;
    void Promise.all([getRecentRead(bookSlug, chapterNum), getLastRead()])
      .then(([recent, last]) => {
        stopScrollInput();
        if (cancelled || readerTookOver()) return;
        restoreVisit.current.restored = true;
        const fromRecent = recent ? recent.scrollOffset : null;
        const fromLast =
          last !== null &&
          last.bookSlug === bookSlug &&
          last.chapter === chapterNum
            ? last.scrollOffset
            : null;
        window.scrollTo(0, fromRecent ?? fromLast ?? 0);
        markReaderSpot();
        chapterHeadingRef.current?.focus({ preventScroll: true });
      })
      .catch(() => {
        stopScrollInput();
        if (cancelled || readerTookOver()) return;
        restoreVisit.current.restored = true;
        window.scrollTo(0, 0);
        chapterHeadingRef.current?.focus({ preventScroll: true });
      });
    return () => {
      cancelled = true;
      stopScrollInput();
    };
  }, [state, bookSlug, chapterNum, targetVerse, reading.restricted, reading.local, reading.ownerId]);

  // Reset the per-jump dismissed flag whenever the jump identity changes (rule
  // #75 documented deviation: the URL ?v target drives it). A fresh navigation
  // to a ?v verse re-shows the gold; a tap suppresses it only for that jump.
  useEffect(() => {
    setDismissed(false);
  }, [bookSlug, chapterNum, targetVerse]);

  // Lookup-session lifecycle (reading-history arc): seed the flag per jump
  // identity, then promote to a normal read on dwell or real scroll input.
  // Promotion writes an immediate non-lookup checkpoint so "Continue reading"
  // updates the moment the user settles in.
  useEffect(() => {
    lookupRef.current = targetVerse !== null;
    if (targetVerse === null || !bookSlug) return;
    let promoted = false;
    const promote = () => {
      if (promoted) return;
      promoted = true;
      lookupRef.current = false;
      void setLastRead(bookSlug, chapterNum, window.scrollY).catch(() => {});
    };
    const timer = setTimeout(promote, 60_000);
    const onInput = () => promote();
    const SCROLL_KEYS = new Set([
      'ArrowDown',
      'ArrowUp',
      'PageDown',
      'PageUp',
      'Home',
      'End',
      ' ',
    ]);
    const onKey = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key)) promote();
    };
    window.addEventListener('wheel', onInput, { passive: true });
    window.addEventListener('touchmove', onInput, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('wheel', onInput);
      window.removeEventListener('touchmove', onInput);
      window.removeEventListener('keydown', onKey);
    };
  }, [bookSlug, chapterNum, targetVerse]);

  useEffect(() => {
    setReturnDismissed(false);
  }, [bookSlug, chapterNum, targetVerse]);

  // Return-chip target: on a lookup landing, capture the last deep read when
  // it differs from the current chapter. Reset per jump identity (rule #75
  // documented deviation: the URL ?v target drives it).
  useEffect(() => {
    if (targetVerse === null || !bookSlug || (reading.restricted && !reading.local)) return;
    let cancelled = false;
    void getLastRead()
      .then((row) => {
        if (cancelled) return;
        if (!row || (row.bookSlug === bookSlug && row.chapter === chapterNum)) { setReturnTarget(null); return; }
        const name = CANONICAL_SLUG_TO_NAME.get(row.bookSlug);
        if (!name) return;
        setReturnTarget({ accessKey: returnAccessKey, bookSlug: row.bookSlug, bookName: name, chapter: row.chapter });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [bookSlug, chapterNum, targetVerse, reading.restricted, reading.local, returnAccessKey]);

  // Keyboard dismissal of the landing gold (parity with the verse tap). Active
  // only while a landing is showing; defers to any open NavStack sheet so Escape
  // closes the sheet first (the gold stays until a later Escape or a verse tap).
  useEffect(() => {
    if (targetVerse === null || dismissed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const sheetOpen =
        activeEntry !== null ||
        prayingAnchor !== null ||
        composeAnchor !== null ||
        threadVerse !== null;
      if (sheetOpen) return;
      setDismissed(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    targetVerse,
    dismissed,
    activeEntry,
    prayingAnchor,
    composeAnchor,
    threadVerse,
  ]);

  // Layout stabilization A (arc 07.4 R4; trigger reworked 2026-06-11): center
  // the landed ?v verse once, then WATCH for real layout shifts — re-centering
  // only when the verse's DOCUMENT-space position moves (a context block
  // mounting above it, fonts swapping, hydration reflow) — via a continuous rAF
  // watch bounded by an absolute cap (LAYOUT_CAP_MS) so a never-settling layout
  // cannot spin forever. It deliberately does NOT re-scroll when the centering
  // formula merely disagrees with the live scroll: the formula reads
  // visualViewport.height, which the programmatic scrolling itself changes on
  // iOS (address-bar collapse), and fractional iOS scrollY can disagree with
  // the target forever — both made the old formula-polling loop call scrollTo
  // every frame for the full cap (visible flicker). The cost is that an
  // address-bar resize alone no longer triggers a perfect re-center; the verse
  // stays a few px off geometric center, which is invisible. Independent of the
  // gold (which persists until a verse tap). Stops on user takeover (a real
  // scroll INPUT: touch-drag, wheel, or a scroll key) so we never fight the
  // reader. takenOverRef is reset here (a new jump), read by effect B.
  useEffect(() => {
    if (state.kind !== 'ready' || !bookSlug || targetVerse === null) return;
    takenOverRef.current = false;

    let raf = 0;
    let capTimer = 0;

    const takeover = () => {
      takenOverRef.current = true;
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    };
    // Takeover is signaled ONLY by a real scroll INPUT (touch drag, wheel, or a
    // scroll key), never by the raw `scroll` event: on iOS the address-bar
    // collapse fires a large `scroll` with no user input, and treating that as
    // takeover would freeze re-centering right as the context paragraphs push
    // the verse down (the "verse ends up at the bottom" report).
    const onIntent = () => takeover();
    const onKey = (e: KeyboardEvent) => {
      if (SCROLL_KEYS.has(e.key)) takeover();
    };

    // Document-space anchor of the verse (scroll-position- and
    // viewport-height-independent): only a change HERE means layout
    // actually moved the verse.
    const verseDocTop = (): number | null => {
      const el = document.querySelector<HTMLElement>(`[data-verse="${targetVerse}"]`);
      if (!el) return null;
      return Math.round(window.scrollY + el.getBoundingClientRect().top);
    };

    let lastDocTop = verseDocTop();

    const watch = () => {
      raf = 0;
      if (takenOverRef.current) return;
      const docTop = verseDocTop();
      if (
        docTop !== null &&
        (lastDocTop === null ||
          Math.abs(docTop - lastDocTop) > LANDING_SHIFT_EPSILON_PX)
      ) {
        lastDocTop = docTop;
        const top = computeVerseTop(targetVerse);
        if (top !== null && Math.abs(top - window.scrollY) > LANDING_APPLY_EPSILON_PX) {
          window.scrollTo(0, top);
        }
      }
      raf = requestAnimationFrame(watch);
    };

    scrollVerseIntoView(targetVerse);
    lastDocTop = verseDocTop();
    raf = requestAnimationFrame(watch);
    capTimer = window.setTimeout(() => {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0; // stop the watch loop; effect B still re-scrolls on late context
      }
    }, LAYOUT_CAP_MS);

    window.addEventListener('wheel', onIntent, { passive: true });
    window.addEventListener('touchmove', onIntent, { passive: true });
    window.addEventListener('keydown', onKey);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (capTimer) clearTimeout(capTimer);
      window.removeEventListener('wheel', onIntent);
      window.removeEventListener('touchmove', onIntent);
      window.removeEventListener('keydown', onKey);
    };
  }, [state.kind, bookSlug, chapterNum, targetVerse, computeVerseTop, scrollVerseIntoView]);

  // Layout stabilization B (arc 07.4 R4): a one-shot re-scroll on each book/
  // chapter context state transition. A context paragraph mounts ABOVE the
  // verses and pushes them down; this re-centers the verse the moment it does.
  // NOT bounded by the LAYOUT_CAP_MS cap, so context that resolves late (e.g. a
  // slow Supabase fetch at 8s) still keeps the verse in view (codex round-2 fix).
  // Gated on no user takeover (shared ref with effect A) so it never fights the
  // reader. rAF-deferred so the new layout has painted before measuring.
  useEffect(() => {
    if (state.kind !== 'ready' || !bookSlug || targetVerse === null) return;
    if (takenOverRef.current) return;
    let raf = requestAnimationFrame(() => {
      raf = 0;
      if (takenOverRef.current) return;
      scrollVerseIntoView(targetVerse);
    });
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [
    bookContextState,
    chapterContextState,
    state.kind,
    bookSlug,
    chapterNum,
    targetVerse,
    scrollVerseIntoView,
  ]);

  useEffect(() => {
    if (state.kind !== 'ready' || !bookSlug) return;
    let rafId: number | null = null;
    let lastWriteAt = 0;
    let lastObservedOffset = window.scrollY;
    // Scroll anchoring moves scrollY by the page-height change when a context
    // placeholder above the verses collapses or grows after a restore. That
    // shift is not the reader's choice: keep the checkpoint on the spot.
    const observe = () => {
      const y = window.scrollY;
      const height = document.documentElement.scrollHeight;
      const spot = readerSpotRef.current;
      if (spot && height !== spot.height
        && Math.abs((y - spot.y) - (height - spot.height)) <= 1) {
        lastObservedOffset = spot.y;
        return;
      }
      readerSpotRef.current = { y, height };
      lastObservedOffset = y;
    };

    // Topmost verse still visible below the sticky header, recorded on each
    // checkpoint so history rows can show "near verse N". [data-verse] nodes
    // are in document order; runs at most ~1 Hz (the write throttle).
    const nearestVisibleVerse = (): number | undefined => {
      const headerBottom = stuckHeaderBottom();
      const nodes = document.querySelectorAll<HTMLElement>('[data-verse]');
      for (const el of nodes) {
        if (el.getBoundingClientRect().bottom > headerBottom + 8) {
          const v = Number.parseInt(el.dataset.verse ?? '', 10);
          return Number.isInteger(v) && v >= 1 ? v : undefined;
        }
      }
      return undefined;
    };

    const persist = () => {
      void setLastRead(bookSlug, chapterNum, lastObservedOffset, {
        verse: nearestVisibleVerse(),
        lookup: lookupRef.current,
      }).catch(() => {});
    };

    const writeIfDue = () => {
      const now = Date.now();
      if (now - lastWriteAt < 1000) return;
      lastWriteAt = now;
      observe();
      persist();
    };

    const onScroll = () => {
      observe();
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        observe();
        writeIfDue();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener('scroll', onScroll);
      observe();
      persist();
    };
  }, [state.kind, bookSlug, chapterNum, stuckHeaderBottom]);

  // Prefetch the book context row on mount + on book change. Cancellation
  // flag prevents a stale resolve from overwriting state after the user
  // navigated to a different book mid-flight.
  //
  // Locale-unsupported branch short-circuits the network round-trip and
  // settles state synchronously so the disclosure (gated on the same
  // bookContextSupported flag at the render site) stays in lock-step.
  //
  // The synchronous setBookContextState('loading') + setBookContextRow(null)
  // on the supported path are intentional: bookName drives the re-fetch
  // and the loading state needs to render immediately on navigation.
  // Mirrors the startLoad effect's documented deviation higher up.
  useEffect(() => {
    if (!bookContextSupported || getReadingAccessSnapshot().restricted) {
      // Locale not supported (no seed data); short-circuit to ready+null so
      // the parent does not render the disclosure and no Supabase round-trip
      // fires. Synchronous setState in effect is the f.1.b precedent for
      // bookName-driven re-fetch; documented deviation, not a refactor target
      // in this chunk.
      setBookContextState('ready');
      setBookContextRow(null);
      return;
    }
    if (!bookName) return;
    let cancelled = false;
    setBookContextState('loading');
    setBookContextRow(null);
    fetchBookContext(bookName, taxonomyLocale)
      .then((result) => {
        if (cancelled) return;
        setBookContextRow(result);
        setBookContextState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[scripture] book context fetch failed:', err);
        setBookContextRow(null);
        setBookContextState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [bookName, taxonomyLocale, bookContextSupported]);

  // Mirrors the book-context prefetch effect's documented deviation.
  // Synchronous setState in effect is the f.1.b precedent for
  // bookName-driven re-fetch; documented deviation, not a refactor target
  // in this chunk.
  useEffect(() => {
    if (!chapterContextSupported || getReadingAccessSnapshot().restricted) {
      setChapterContextState('ready');
      setChapterContextRow(null);
      return;
    }
    if (!bookName) return;
    let cancelled = false;
    setChapterContextState('loading');
    setChapterContextRow(null);
    fetchChapterContext(bookName, chapterNum, taxonomyLocale)
      .then((result) => {
        if (cancelled) return;
        setChapterContextRow(result);
        setChapterContextState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[scripture] chapter context fetch failed:', err);
        setChapterContextRow(null);
        setChapterContextState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [bookName, chapterNum, taxonomyLocale, chapterContextSupported]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      // The docked verse panel (verse-panel interaction redesign) is
      // role="region", NOT role="dialog", so the dialog guard below does not
      // cover it. While the panel is open, suppress chapter arrow-nav entirely
      // (focus on a panel button OR a selected verse run). versePanelOpen is
      // v2-only, so this guard is inert under flag-off.
      if (versePanelOpen) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (target.isContentEditable) return;
        if (target.closest('[role="dialog"]')) return;
      }
      if (e.key === 'ArrowLeft' && prev) {
        e.preventDefault();
        navigate(`/bible/${prev.bookSlug}/${prev.chapter}`);
      } else if (e.key === 'ArrowRight' && next) {
        e.preventDefault();
        navigate(`/bible/${next.bookSlug}/${next.chapter}`);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prev, next, navigate, versePanelOpen]);

  // Decide matches over the complete verse, then retain every original layout
  // and red-letter boundary when rendering the projected pieces below.
  const glossaryProjection = useMemo(() => {
    if (!GLOSSARY_ENABLED || !contextAllowed || !studyMode || !headwords || state.kind !== 'ready'
      || !occurrences || occurrences.slug !== bookSlug) return null;
    return projectChapterGlossary(state.blocks, occurrences.map, chapterNum);
  }, [studyMode, headwords, state, occurrences, bookSlug, chapterNum, contextAllowed]);

  const renderWithGlossary = (
    raw: string,
    keyPrefix: string,
    segments?: ProjectedGlossarySegment[],
  ): ReactNode => {
    if (!segments || !headwords) return raw;
    return segments.map((seg, idx) => {
      if (seg.kind === 'plain' || !headwords.has(seg.entryId)) return seg.text;
      return (
        <button
          type="button"
          key={`${keyPrefix}-g-${idx}`}
          className={styles.glowWord}
          data-glossary-entry={seg.entryId}
          onClick={(e) => {
            e.stopPropagation();
            const entry = headwords.get(seg.entryId);
            if (entry) setActiveEntry(entry);
          }}
          aria-label={t('glossary.tapToDefineAria', { word: seg.phraseText })}
        >
          {seg.text}
        </button>
      );
    });
  };

  function renderVerseText(verse: BsbVerse, allowGlossary: boolean): ReactNode {
    const projected = allowGlossary ? glossaryProjection?.get(verse) : undefined;
    if (typeof verse.t === 'string') {
      return renderWithGlossary(verse.t, 'vt-str', projected?.[0]);
    }
    return verse.t.map((seg, i) => {
      if (seg.kind === 'wj') {
        return (
          <span key={i} className={styles.wj}>
            {renderWithGlossary(seg.text, `wj-${i}`, projected?.[i])}
            <span className={styles.srOnly}>
              {' '}
              ({t('bible.wordsOfJesusSr')})
            </span>
          </span>
        );
      }
      return (
        <span key={i}>
          {renderWithGlossary(seg.text, `vt-${i}`, projected?.[i])}
        </span>
      );
    });
  }

  // v2 per-verse render (Chunk B). The verse <span> is NEVER a role="button" in
  // the normal branch (the §3.2 nesting resolution), so glow words COEXIST with
  // the verse-number handle. Used only under BIBLE_READER_V2_ENABLED; the v1
  // renderVerse branches are the untouched flag-off path.
  function renderVerseV2(
    verse: BsbVerse,
    key: string | number,
    isHighlight: boolean,
  ): ReactNode {
    const noteCount = verseBookmarkCounts.get(verse.v); // undefined if unsaved
    // No !studyMode/!prayMode gate: those modes do not exist under v2, and the
    // verse run is no longer a button, so a saved verse shows its tint + ribbon
    // WHILE Study glow is on (ribbon, number, and glow words are sibling
    // buttons; no nesting).
    const showBookmarkV2 = BIBLE_BOOKMARKS_ENABLED && noteCount !== undefined;

    // v2 panel-open branch (verse-panel interaction redesign): while the docked
    // panel is open, the verse run is a CONTIGUOUS-selection extension target
    // (role="button", so glow words are suppressed via the null allowed-set
    // below -- a role="button" run cannot nest the glow-word buttons, the rule
    // #82 nesting hazard; default reading keeps glow live). Reuses the existing
    // JSX shape + aria keys. data-verse first-fragment-only.
    if (versePanelOpen) {
      const selected = selectedVerses.has(verse.v);
      // Keep an already-highlighted verse its PICKED family color while it is
      // (re-)selected to view / change / remove (operator UX call 2026-06-20: a
      // highlighted verse used to flip to the gold selection wash on tap, losing
      // its color until actually changed). The family wash owns the bg; the gold
      // selection wash (.verseSelected) applies ONLY to an unhighlighted verse; a
      // selected highlighted verse instead gets the additive gold ring
      // (.verseSelectedRing, a non-bg inset shadow) so its selected state still
      // reads without overriding the color. coerceFamily keeps undefined for an
      // unhighlighted verse and maps a present orphan slug to 'plain' (rule #55).
      const selFamily = coerceFamily(verseHighlights.get(verse.v)?.family);
      const verseClass = [
        styles.verse,
        styles.verseSelectable,
        selFamily ? `${styles.verseHl} ${HL_WASH_CLASS[selFamily]}` : '',
        selected && !selFamily ? styles.verseSelected : '',
        selected && selFamily ? styles.verseSelectedRing : '',
      ]
        .filter(Boolean)
        .join(' ');
      return (
        <span
          key={key}
          role="button"
          tabIndex={0}
          aria-pressed={selected}
          aria-label={t(
            selected ? 'bible.verseSelectedAria' : 'bible.verseSelectAria',
            { v: verse.v },
          )}
          data-verse={!verse.vCont ? verse.v : undefined}
          className={verseClass}
          onClick={() => extendSelectionTo(verse.v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              extendSelectionTo(verse.v);
            }
          }}
        >
          {!verse.vCont && (
            <span className={styles.verseNum} aria-hidden="true">
              {verse.v}
            </span>
          )}
          {renderVerseText(verse, false)}
        </span>
      );
    }

    // v2 normal verse (verse-tap redesign §2/§3): the verse RUN is the tap
    // target. Tapping an UNSAVED verse enters the selection sub-mode and toggles
    // it (the slim Keep/Pray/Share bar appears); tapping a KEPT verse opens its
    // thread (the rich view). The run stays a non-interactive <span> (no role /
    // tabIndex) so it never nests with the glow-word buttons (glossary intact);
    // the verse NUMBER is the keyboard/SR handle carrying the SAME action. The
    // saved-verse gold marker is the .verseBookmarked tint (the separate ribbon
    // button is retired under the redesign -- tap the verse, not a tiny ribbon).
    // The per-verse first-fragment data-verse anchor + the search-landing
    // classes/dismiss are preserved in every state.
    // Meaning-first highlight wash (Chunk C, §5.B4). undefined when unhighlighted;
    // coerceFamily keeps undefined for an absent verse and maps a present orphan
    // (a dropped 'wonder' test row) to 'plain' (rule #55).
    const family = coerceFamily(verseHighlights.get(verse.v)?.family);
    const spanClass = [
      styles.verse,
      // BG layer: a family wash wins the bg when present; otherwise the kept gold
      // tint (unchanged). A family wash + kept verse ALSO gets .verseKeptCue, the
      // additive gold left-rail, so the saved state stays visible (pass-1 BLOCKER 1).
      family
        ? `${styles.verseHl} ${HL_WASH_CLASS[family]}`
        : showBookmarkV2
          ? styles.verseBookmarked
          : '',
      family && showBookmarkV2 ? styles.verseKeptCue : '',
      // search-landing TEXT-glow: applies in EVERY landed state (pass-1 BLOCKER
      // 2: parity with the saved-verse precedent, which keeps .verseLanding and
      // drops only the landing BG). NOT suppressed by a family wash.
      isHighlight ? styles.verseLanding : '',
      // search-landing BG: suppressed when a family wash OR the kept tint owns
      // the bg (avoids double-tint), exactly as the live code already drops it
      // under .verseBookmarked.
      isHighlight && !family && !showBookmarkV2 ? styles.verseHighlight : '',
    ]
      .filter(Boolean)
      .join(' ');
    // One action for both the run (pointer) and the number (keyboard/SR):
    // tapping any verse (saved, highlighted, noted, or plain) opens the unified
    // verse panel for that single verse. The panel reflects state (active color
    // + Remove, Notes (N)). Clears the search-landing gold first, as before.
    const openVerse = (focusOnOpen: boolean) => {
      if (isHighlight) dismissLanding();
      // First-run coachmark: the user found the gesture, so stop teaching it
      // (idempotent; also dismissed by the banner's explicit X).
      if (contextAllowed && !verseCoachmarkSeen) markVerseCoachmarkSeen();
      setAnchorVerse(verse.v);
      setSelectedVerses(new Set([verse.v]));
      setVersePanelFocusOnOpen(focusOnOpen);
      setHighlightMutationFailed(false);
      setVersePanelOpen(true);
      // Keep the tapped verse visible above the docked panel (verse-panel
      // follow-up). rAF lets the spacer mount before the geometry read.
      requestAnimationFrame(() => scrollVerseAbovePanel(verse.v));
    };
    return (
      <span
        key={key}
        className={spanClass}
        data-verse={!verse.vCont ? verse.v : undefined}
        onClick={() => openVerse(false)}
      >
        {!verse.vCont && (
          <button
            type="button"
            ref={verse.v === anchorVerse ? versePanelReturnFocusRef : undefined}
            className={styles.verseNumButton}
            aria-label={t('bible.verseOpenPanelAria', { v: verse.v })}
            onClick={(e) => {
              e.stopPropagation();
              openVerse(e.detail === 0);
            }}
          >
            {verse.v}
          </button>
        )}
        {renderVerseText(verse, true)}
      </span>
    );
  }

  function renderVerse(verse: BsbVerse, key: string | number): ReactNode {
    if (verse.omitted) {
      return (
        <span
          key={key}
          className={styles.omittedVerse}
          aria-label={t('bible.verseOmittedSr', { v: verse.v })}
        >
          <span className={styles.verseNum} aria-hidden="true">
            [{verse.v}]
          </span>
        </span>
      );
    }
    // Search-landing gold (arc 07.4 R4): the verse is golded when the LIVE URL
    // ?v= equals this verse and the user has not dismissed THIS jump. Computed
    // once here so the bookmark-open, vCont-saved, and plain branches below all
    // share it. Selection-mode verses are out of scope (a search landing always
    // opens in default reading mode).
    const isHighlight = targetVerse !== null && verse.v === targetVerse && !dismissed;

    // v2 (Chunk B): the verse-tap branch set replaces the v1 mode branches
    // below. The omitted branch above + isHighlight are shared; everything from
    // here down is the untouched flag-off v1 path.
    if (BIBLE_READER_V2_ENABLED) {
      return renderVerseV2(verse, key, isHighlight);
    }

    // Bookmark thread (thread-redesign §4.2): a SAVED verse is a tappable
    // run that opens its thread of notes. Visible + openable in normal
    // reading AND bookmark mode; hidden in Study mode (glow words are real
    // buttons there -> nesting, Revision 3 BLOCKER 1) and Pray mode (its own
    // focused selection). studyMode/prayMode are the BibleChapter-local
    // aliases. Takes precedence over the selection + plain branches for
    // saved verses, so a saved verse OPENS (never selects) in bookmark mode
    // and is never both tinted and selection-washed (D7/D13).
    const noteCount = verseBookmarkCounts.get(verse.v); // undefined if unsaved
    const isBookmarked = BIBLE_BOOKMARKS_ENABLED && noteCount !== undefined;
    const showBookmark = isBookmarked && !(contextAllowed && studyMode) && !prayMode;
    if (showBookmark && !verse.vCont) {
      // First fragment of a saved verse: tint + gold number + plain text +
      // a decorative gold ribbon cue at the end; the verse RUN is the
      // labeled tap target (parity with the selection-mode verse button,
      // D8). null allowed-set -> renderVerseText emits plain text with NO
      // glow-word buttons, so no nested interactive controls.
      return (
        <span
          key={key}
          role="button"
          tabIndex={0}
          aria-label={t('bible.bookmarkOpenAria', {
            reference: verse.v,
            count: noteCount ?? 0,
          })}
          data-verse={verse.v}
          className={`${styles.verse} ${styles.verseBookmarked} ${styles.verseBookmarkOpen}${
            isHighlight ? ` ${styles.verseLanding}` : ''
          }`}
          onClick={() => {
            if (isHighlight) dismissLanding();
            setThreadVerse(verse.v);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (isHighlight) dismissLanding();
              setThreadVerse(verse.v);
            }
          }}
        >
          <span className={styles.verseNum} aria-hidden="true">
            {verse.v}
          </span>
          {renderVerseText(verse, false)}
          <span className={styles.bookmarkCue} aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 2h12a1 1 0 0 1 1 1v18l-7-4-7 4V3a1 1 0 0 1 1-1z" />
            </svg>
          </span>
        </span>
      );
    }
    if (showBookmark && verse.vCont) {
      // Continuation fragment of a saved verse: plain text but carry the
      // tint so the whole verse reads as saved (no button, no cue). D6.
      // When this is the landed verse, also gold it + allow tap-to-dismiss.
      return (
        <span
          key={key}
          className={`${styles.verse} ${styles.verseBookmarked}${
            isHighlight ? ` ${styles.verseLanding}` : ''
          }`}
          onClick={isHighlight ? dismissLanding : undefined}
        >
          {renderVerseText(verse, false)}
        </span>
      );
    }
    // Pray / Bookmark Mode selection: the verse run is a highlighter toggle.
    // Reached now only by pray-mode verses + bookmark-mode UNSAVED verses
    // (saved verses returned above). The two modes are mutually exclusive
    // with each other and with Study Mode, so the glossary glow never
    // coexists here and the text renders plain (null allowed-set). The
    // verse-level aria-label carries the number + action, so the inner verse
    // number is aria-hidden to avoid a double announcement. The data-verse
    // anchor (first fragment only) is consumed by computeVerseTop (the
    // Bible-search ?v= jump, and the Bible-bookmarks Phase 4 jump-to-verse
    // share the same helper).
    const selectionMode =
      (PRAY_MODE_ENABLED && prayMode) ||
      (BIBLE_BOOKMARKS_ENABLED && bookmarkMode);
    if (selectionMode) {
      const selected = selectedVerses.has(verse.v);
      const verseClass = `${styles.verse} ${styles.verseSelectable} ${
        selected ? styles.verseSelected : ''
      }`.trim();
      const ariaKey = bookmarkMode
        ? selected
          ? 'bible.verseBookmarkSelectedAria'
          : 'bible.verseBookmarkAria'
        : selected
          ? 'bible.verseSelectedAria'
          : 'bible.verseSelectAria';
      return (
        <span
          key={key}
          role="button"
          tabIndex={0}
          aria-pressed={selected}
          aria-label={t(ariaKey, { v: verse.v })}
          data-verse={!verse.vCont ? verse.v : undefined}
          className={verseClass}
          onClick={() => toggleVerse(verse.v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleVerse(verse.v);
            }
          }}
        >
          {!verse.vCont && (
            <span className={styles.verseNum} aria-hidden="true">
              {verse.v}
            </span>
          )}
          {renderVerseText(verse, false)}
        </span>
      );
    }
    // Plain branch (thread-redesign §4.2, Revision 4 BLOCKER): normal-reading
    // UNSAVED verses + ALL Study-mode verses (glow via the chapter projection).
    // STRIPPED of every bookmark visual (no tint / ribbon / gold number) so a
    // saved verse in Study or Pray mode (showBookmark === false) renders with
    // zero bookmark cues, honoring D12. Bookmark visuals live ONLY in the OPEN
    // + vCont-saved branches above.
    // Search-landing gold (arc 07.4 R4): the whole landed verse renders gold
    // (`.verseLanding`) + a faint tint (`.verseHighlight`) so it is unmistakable,
    // and a tap on the verse dismisses it (`dismissLanding`). isHighlight derives
    // off the LIVE ?v target + the per-jump dismissed flag, so a same-mount param
    // change to a ?v-less route shows nothing and there is no stale target.
    return (
      <span
        key={key}
        className={
          isHighlight
            ? `${styles.verse} ${styles.verseLanding} ${styles.verseHighlight}`
            : styles.verse
        }
        data-verse={!verse.vCont ? verse.v : undefined}
        onClick={isHighlight ? dismissLanding : undefined}
      >
        {!verse.vCont && (
          <span
            className={styles.verseNum}
            aria-label={t('bible.verseNumSr', { v: verse.v })}
          >
            {verse.v}
          </span>
        )}
        {renderVerseText(verse, true)}
      </span>
    );
  }

  function renderBlock(block: BsbBlock, idx: number): ReactNode {
    switch (block.type) {
      case 'paragraph': {
        const className = `${styles.paragraph} ${MARKER_CLASS[block.marker]}`;
        return (
          <p key={idx} className={className}>
            {block.verses.map((v, i) => renderVerse(v, `${idx}-${i}`))}
          </p>
        );
      }
      case 'poetry': {
        return (
          <div key={idx} className={styles.poetry}>
            {block.lines.map((line, li) => {
              const indentClass =
                line.indent === 1
                  ? styles.poetryIndent1
                  : line.indent === 2
                    ? styles.poetryIndent2
                    : styles.poetryIndentRight;
              return (
                <div
                  key={li}
                  className={`${styles.poetryLine} ${indentClass}`}
                >
                  {line.verses.map((v, i) =>
                    renderVerse(v, `${idx}-${li}-${i}`),
                  )}
                </div>
              );
            })}
          </div>
        );
      }
      case 'acrostic': {
        if (block.text.length <= 1) {
          return (
            <div key={idx} className={styles.acrosticGlyph} aria-hidden="true">
              {block.text}
            </div>
          );
        }
        return (
          <div
            key={idx}
            className={styles.acrosticLabel}
            aria-label={t('bible.stanzaMarkerSr', { marker: block.text })}
          >
            {block.text}
          </div>
        );
      }
      case 'title': {
        return (
          <div key={idx} className={styles.bookTitle}>
            {block.text}
          </div>
        );
      }
      case 'majorSection': {
        return (
          <div key={idx} className={styles.majorSection}>
            {block.text}
          </div>
        );
      }
      case 'section': {
        if (block.level === 1) {
          return (
            <h2 key={idx} className={styles.section1}>
              {block.text}
            </h2>
          );
        }
        return (
          <h3 key={idx} className={styles.section2}>
            {block.text}
          </h3>
        );
      }
      case 'reference': {
        return (
          <p key={idx} className={styles.reference}>
            {block.text}
          </p>
        );
      }
      case 'blank': {
        return <div key={idx} className={styles.blank} aria-hidden="true" />;
      }
      default: {
        const _exhaustive: never = block;
        return _exhaustive;
      }
    }
  }

  if (!inputsValid) {
    return (
      <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
        <div className={styles.readerBg} aria-hidden="true" />
        <div className={styles.container} data-bible-theme={bibleTheme}>
        <Link to="/bible" className={styles.backLink}>
          {t('bible.back')}
        </Link>
        <div
          ref={noticeReserveRef}
          className={styles.noticeReserve}
          style={{ height: noticeReserve }}
          data-notice-reserve={noticeReserve}
          aria-hidden="true"
        />
        <header className={styles.header}>
          <h1 className={styles.chapterHeading}>
            <span className={styles.bookName}>{t('bible.title')}</span>
          </h1>
        </header>
        <button
          type="button"
          className={styles.retry}
          onClick={startLoad}
        >
          {t('bible.chapterReaderLoadFailed')}
        </button>
        </div>
      </div>
    );
  }

  const selectedCount = selectedVerses.size;
  // v1 bars are retired under v2 (Chunk B). prayMode/bookmarkMode can never be
  // true under v2 anyway (their toggles are hidden + the per-chapter reset
  // clears them), but the explicit !v2 gate documents the dual-path intent.
  const showPrayBar =
    !readOnly &&
    !BIBLE_READER_V2_ENABLED &&
    PRAY_MODE_ENABLED &&
    prayMode &&
    state.kind === 'ready';
  // Bookmark bar is hidden at 0 selected (whole-chapter bookmarking is cut,
  // thread-redesign §4.5); the teaching hint carries guidance until the user
  // taps a verse. 1+ selected -> Clear + "Bookmark N verses". Mutually
  // exclusive with Pray, so the two bars never co-render.
  const showBookmarkBar =
    !readOnly &&
    !BIBLE_READER_V2_ENABLED &&
    BIBLE_BOOKMARKS_ENABLED &&
    bookmarkMode &&
    state.kind === 'ready' &&
    selectedCount > 0;
  // Live selection citation for the panel header (verse-panel interaction
  // redesign): buildScriptureAnchor over the present selection -> its .reference
  // ("John 3:16" -> "John 3:16-19" as the user taps), '' when empty / not ready.
  // A plain const (not a hook) like its selection-derived siblings below, so it
  // sits cleanly after the component's early returns.
  const selectionReference =
    state.kind === 'ready' && bookSlug && selectedVerses.size > 0
      ? (buildScriptureAnchor({
          bookSlug,
          chapter: chapterNum,
          blocks: state.blocks,
          selection: { kind: 'verses', verses: [...selectedVerses] },
        })?.reference ?? '')
      : '';
  // Verse-panel derived state. canRemove: any selected
  // verse already carries a highlight. activeFamily: the family marked active in
  // the sheet -- non-null ONLY when EVERY selected verse is highlighted (no
  // `undefined` in the set, i.e. no plain verse selected) AND they all share one
  // family (pass-2 LOW 1); a mixed selection resolves to null.
  const selectionHasAnyHighlight = [...selectedVerses].some((v) =>
    verseHighlights.has(v),
  );
  const selectionAllSameFamily = (() => {
    if (selectedVerses.size === 0) return null;
    const fams = new Set(
      [...selectedVerses].map((v) => coerceFamily(verseHighlights.get(v)?.family)),
    );
    return fams.size === 1 && !fams.has(undefined)
      ? ([...fams][0] as HighlightFamily)
      : null;
  })();
  // The header always stacks to two lines on narrow screens so a long book name
  // never wraps. Unconditional now (Chunk A): the theme picker is always present
  // in the right-slot .headerActions wrapper, so the right slot always has
  // content regardless of the mode flags. The .headerBarStacked class only
  // changes layout inside the <=600px media query, so wide screens are
  // unaffected.
  const headerCanStack = true;

  return (
    <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
      <div className={styles.readerBg} aria-hidden="true" />
      <div className={styles.container} data-bible-theme={bibleTheme}>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />
      <div
        ref={stickyGroupRef}
        className={styles.stickyGroup}
        style={
          noticeClearance > 0
            ? stickBelowNotices
              ? { top: `calc(${noticeClearance}px - env(safe-area-inset-top, 0px))` }
              : { position: 'relative' }
            : undefined
        }
        data-notice-sticky={noticeClearance > 0 ? (stickBelowNotices ? 'below' : 'off') : undefined}
      >
        <header
          className={`${styles.headerBar} ${headerCanStack ? styles.headerBarStacked : ''}`.trim()}
        >
          <Link to="/bible" className={styles.backLink}>
            {t('bible.allBooks')}
          </Link>
          <nav
            className={styles.chapterGroup}
            aria-label={t('bible.chapterNavAria')}
          >
            {prev ? (
              <Link
                to={`/bible/${prev.bookSlug}/${prev.chapter}`}
                className={styles.chapterChevron}
                aria-label={t('bible.prevChapterAria', {
                  book: prev.bookName,
                  chapter: prev.chapter,
                })}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </Link>
            ) : (
              <span
                className={styles.chapterChevronPlaceholder}
                aria-hidden="true"
              />
            )}
            <h1
              ref={chapterHeadingRef}
              tabIndex={-1}
              className={styles.chapterHeading}
            >
              <span className={styles.bookName}>{bookName}</span>
              <span className={styles.chapterNumber}>{chapterNum}</span>
            </h1>
            {next ? (
              <Link
                to={`/bible/${next.bookSlug}/${next.chapter}`}
                className={styles.chapterChevron}
                aria-label={t('bible.nextChapterAria', {
                  book: next.bookName,
                  chapter: next.chapter,
                })}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </Link>
            ) : (
              <span
                className={styles.chapterChevronPlaceholder}
                aria-hidden="true"
              />
            )}
          </nav>
          <div className={styles.headerActions}>
            <BibleThemePicker />
            <BibleFontSizePicker />
            {/* v2 (Chunk B): under BIBLE_READER_V2_ENABLED the Pray + Bookmark
                toggles are retired (their capabilities live in the verse-action
                sheet); only the Study toggle stays, beside the always-present
                theme picker. */}
            {((GLOSSARY_ENABLED && contextAllowed && manifestBooks) ||
            (!readOnly && !BIBLE_READER_V2_ENABLED &&
              (PRAY_MODE_ENABLED || BIBLE_BOOKMARKS_ENABLED))) && (
            <div className={styles.modeToggleGroup}>
              {GLOSSARY_ENABLED && contextAllowed && manifestBooks && (
                <button
                  type="button"
                  className={`${styles.modeToggle} ${studyMode ? styles.modeToggleActive : ''}`.trim()}
                  onClick={handleToggleStudyMode}
                  aria-pressed={studyMode}
                >
                  {t('bible.studyMode')}
                </button>
              )}
              {!readOnly && !BIBLE_READER_V2_ENABLED && PRAY_MODE_ENABLED && (
                <button
                  type="button"
                  className={`${styles.modeToggle} ${prayMode ? styles.modeToggleActive : ''}`.trim()}
                  onClick={handleTogglePray}
                  aria-pressed={prayMode}
                >
                  {t('bible.prayMode')}
                </button>
              )}
              {!readOnly && !BIBLE_READER_V2_ENABLED && BIBLE_BOOKMARKS_ENABLED && (
                <button
                  type="button"
                  className={`${styles.modeToggle} ${bookmarkMode ? styles.modeToggleActive : ''}`.trim()}
                  onClick={handleToggleBookmark}
                  aria-pressed={bookmarkMode}
                >
                  {t('bible.bookmarkMode')}
                </button>
              )}
            </div>
            )}
          </div>
        </header>
        {/* Persistent mode-indicator banner, pinned INSIDE the sticky group so
            it rides along on scroll and stays the whole mode session (no
            auto-dismiss, no tap-dismiss). At most one renders (modes are
            mutually exclusive). computeVerseTop reads the live sticky height,
            so the taller bar is absorbed by the ?v= scroll automatically. The
            Study banner content is coverage-gated: on a book NOT in the
            glossary manifest, nothing can glow, so it shows the no-coverage
            copy instead of "tap any glowing word". */}
        {/* Chunk D: one-time first-run coachmark teaching the verse-as-handle
            gesture (tapping the whole verse run opens the panel — openVerse
            above). v2-only; dismissed on the X or first panel open; reactive,
            so it hides immediately. Pinned in the sticky group like the mode
            hints, but with a close X and a one-time seen-flag. */}
        {/* Return chip (reading-history arc): after a verse lookup, one tap
            back to the chapter the user was deep-reading. */}
        {activeReturnTarget && !returnDismissed && (
          <div className={styles.returnChip}>
            <Link
              to={`/bible/${activeReturnTarget.bookSlug}/${activeReturnTarget.chapter}`}
              className={styles.returnChipLink}
              aria-label={t('bible.returnToReadingAria', {
                book: activeReturnTarget.bookName,
                chapter: activeReturnTarget.chapter,
              })}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span className={styles.returnChipText}>
                {t('bible.returnToReading', {
                  book: activeReturnTarget.bookName,
                  chapter: activeReturnTarget.chapter,
                })}
              </span>
            </Link>
            <button
              type="button"
              className={styles.returnChipClose}
              aria-label={t('bible.returnDismissAria')}
              onClick={() => setReturnDismissed(true)}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </button>
          </div>
        )}
        {contextAllowed && BIBLE_READER_V2_ENABLED && !verseCoachmarkSeen && state.kind === 'ready' && (
          <div className={styles.verseCoachmark} role="status" aria-live="polite">
            <span className={styles.verseCoachmarkText}>
              {t('bible.verseCoachmark')}
            </span>
            <button
              type="button"
              className={styles.verseCoachmarkClose}
              aria-label={t('bible.verseCoachmarkDismissAria')}
              onClick={markVerseCoachmarkSeen}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </button>
          </div>
        )}
        {GLOSSARY_ENABLED && contextAllowed && studyMode && manifestBooks && (
          <div
            className={styles.studyModeHintToast}
            role="status"
            aria-live="polite"
          >
            {!manifestBooks.has(bookSlug)
              ? t('bible.studyModeNoCoverage')
              : currentGlossaryError
                ? t('bible.studyModeError')
                : !headwords || occurrences?.slug !== bookSlug
                  ? t('bible.studyModeLoading')
                  : t('bible.studyModeHint')}
            {manifestBooks.has(bookSlug) && currentGlossaryError && (
              <button
                type="button"
                className={styles.glossaryRetry}
                onClick={() => {
                  setHeadwordsError(false);
                  setOccurrencesError(null);
                }}
              >
                {t('glossary.retry')}
              </button>
            )}
          </div>
        )}
        {!BIBLE_READER_V2_ENABLED && PRAY_MODE_ENABLED && prayMode && (
          <div
            className={styles.prayModeHintToast}
            role="status"
            aria-live="polite"
          >
            {t('bible.prayModeHint')}
          </div>
        )}
        {!BIBLE_READER_V2_ENABLED && BIBLE_BOOKMARKS_ENABLED && bookmarkMode && (
          <div
            className={styles.prayModeHintToast}
            role="status"
            aria-live="polite"
          >
            {t('bible.bookmarkModeHint')}
          </div>
        )}
      </div>
      {bookContextSupported && (
        <BibleContextBlock
          key={`${bookSlug}-${chapterNum}`}
          kind="book"
          bookSlug={bookSlug}
          bookName={bookTitle ?? bookName}
          chapter={chapterNum}
          contextText={bookContextRow?.bookContext ?? null}
          state={bookContextState === 'idle' ? 'loading' : bookContextState}
          onPersistedChange={(collapsed) =>
            setBookContextCollapsed(bookSlug, collapsed)
          }
        />
      )}
      {chapterContextSupported && (
        <BibleContextBlock
          key={`${bookSlug}-${chapterNum}-chapter`}
          kind="chapter"
          bookSlug={bookSlug}
          bookName={bookTitle ?? bookName}
          chapter={chapterNum}
          contextText={chapterContextRow?.chapterContext ?? null}
          state={chapterContextState === 'idle' ? 'loading' : chapterContextState}
          onPersistedChange={(collapsed) =>
            setChapterContextCollapsed(bookSlug, chapterNum, collapsed)
          }
        />
      )}
      {state.kind === 'pending' && (
        <p className={styles.status}>{t('bible.preparing')}</p>
      )}
      {state.kind === 'failed' && (
        <button
          type="button"
          className={styles.retry}
          onClick={startLoad}
        >
          {t(
            state.offline
              ? 'bible.offlineNeedsDownload'
              : 'bible.chapterReaderLoadFailed',
          )}
        </button>
      )}
      {state.kind === 'ready' && (
        <main className={styles.reader}>
          {state.blocks.map((block, idx) => renderBlock(block, idx))}
        </main>
      )}
      {showPrayBar && (
        <>
          {/* Spacer keeps the last verse scrollable clear of the fixed bar. */}
          <div className={styles.prayBarSpacer} aria-hidden="true" />
          <div
            className={styles.prayBar}
            role="region"
            aria-label={t('bible.prayBarAria')}
          >
            {/* SR-only running count; the bar itself is not aria-live to
                avoid re-announcing the whole control row on each tap. */}
            <span className={styles.srOnly} aria-live="polite">
              {selectedCount > 0
                ? t('bible.praySelectedCount', { count: selectedCount })
                : ''}
            </span>
            <div className={styles.prayBarInner}>
              {selectedCount === 0 ? (
                <button
                  type="button"
                  className={styles.prayBarPrimary}
                  onClick={handlePrayOnChapter}
                >
                  {t('bible.prayWholeChapter')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className={styles.prayBarClear}
                    onClick={handleClearSelection}
                  >
                    {t('bible.prayClear')}
                  </button>
                  <button
                    type="button"
                    className={styles.prayBarPrimary}
                    onClick={handlePrayOnSelected}
                  >
                    {t('bible.prayOnVerses', { count: selectedCount })}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
      {showBookmarkBar && (
        <>
          {/* Spacer keeps the last verse scrollable clear of the fixed bar.
              Reuses the prayBar chrome (D3): the two bars never co-render
              because Pray and Bookmark modes are mutually exclusive. */}
          <div className={styles.prayBarSpacer} aria-hidden="true" />
          <div
            className={styles.prayBar}
            role="region"
            aria-label={t('bible.bookmarkBarAria')}
          >
            <span className={styles.srOnly} aria-live="polite">
              {t('bible.bookmarkSelectedCount', { count: selectedCount })}
            </span>
            {/* Bar only renders at 1+ selected (whole-chapter cut), so the
                0-selected branch is gone: Clear + "Bookmark N verses". */}
            <div className={styles.prayBarInner}>
              <button
                type="button"
                className={styles.prayBarClear}
                onClick={handleClearSelection}
              >
                {t('bible.bookmarkClear')}
              </button>
              <button
                type="button"
                className={styles.prayBarPrimary}
                onClick={handleBookmarkSelected}
              >
                {t('bible.bookmarkVerses', { count: selectedCount })}
              </button>
            </div>
          </div>
        </>
      )}
      {GLOSSARY_ENABLED && contextAllowed && activeEntry && (
        <GlossarySheet
          entry={activeEntry}
          onClose={() => setActiveEntry(null)}
          zIndex={zGlossary}
        />
      )}
      {!readOnly && PRAY_MODE_ENABLED && prayingAnchor && (
        <AuthorizedReaderSlot id="reader-pray">
        <PrayOnScriptureSheet
          ref={prayRef}
          anchor={prayingAnchor}
          onClose={() => setPrayingAnchor(null)}
          onSaved={handlePraySaved}
          zIndex={zPray}
        />
        </AuthorizedReaderSlot>
      )}
      {/* COMPOSE sheet (selection -> create a passage note). */}
      {!readOnly && BIBLE_BOOKMARKS_ENABLED && composeAnchor && (
        <AuthorizedReaderSlot id="reader-compose">
        <BibleBookmarkSheet
          ref={composeRef}
          mode="compose"
          anchor={composeAnchor}
          voiceTarget={`bookmark-${bookSlug}-${chapterNum}`}
          onClose={() => setComposeAnchor(null)}
          onSaved={handleBookmarkSaved}
          zIndex={zCompose}
        />
        </AuthorizedReaderSlot>
      )}
      {/* THREAD sheet (verse-tap -> all notes on that verse). No onSaved:
          "Add a note" leaves the thread open; it closes only via onClose
          (backdrop / Escape / deleting the last note). */}
      {!readOnly && BIBLE_BOOKMARKS_ENABLED && threadAnchor && (
        <AuthorizedReaderSlot id="reader-thread">
        <BibleBookmarkSheet
          ref={threadRef}
          mode="thread"
          anchor={threadAnchor}
          voiceTarget={`bookmark-thread-${bookSlug}-${chapterNum}-${threadVerse}`}
          onClose={() => setThreadVerse(null)}
          zIndex={zThread}
        />
        </AuthorizedReaderSlot>
      )}
      {readOnly && !BIBLE_READER_V2_ENABLED && BIBLE_BOOKMARKS_ENABLED && threadAnchor && (
        <BibleReadonlyNotes
          anchor={threadAnchor}
          notes={bookmarks.filter((note) => note.bookSlug === bookSlug && note.chapter === chapterNum &&
            (note.anchor.wholeChapter || note.anchor.verses.includes(threadVerse!)))}
          onClose={() => nav.requestClose('bookmark-thread')}
          zIndex={zThread}
        />
      )}
      {/* Docked verse panel + reader spacer (verse-panel interaction redesign):
          tapping a verse opens this NON-MODAL docked panel; the page stays live
          and scrollable above it. The in-flow .versePanelSpacer keeps the last
          verse scrollable clear of the fixed panel (mirrors .prayBarSpacer).
          Color stacks (panel stays open); Note + Pray defer-open (close panel,
          then open after the pop lands); Share is direct; Done dismisses. */}
      {BIBLE_READER_V2_ENABLED && versePanelOpen && (
        <>
          <div className={styles.versePanelSpacer} aria-hidden="true" />
          <VersePanelSheet
            reference={selectionReference}
            activeFamily={selectionAllSameFamily}
            canRemove={selectionHasAnyHighlight}
            noteCount={
              selectedVerses.size === 1
                ? (verseBookmarkCounts.get([...selectedVerses][0]) ?? 0)
                : 0
            }
            isRange={selectedVerses.size > 1}
            readOnlyNotes={reading.restricted ? bookmarks.filter((note) =>
              note.bookSlug === bookSlug && note.chapter === chapterNum &&
              (note.anchor.wholeChapter || note.anchor.verses.some((verse) => selectedVerses.has(verse)))) : undefined}
            mutationsEnabled={!readOnly}
            bookmarksEnabled={!readOnly && BIBLE_BOOKMARKS_ENABLED}
            prayEnabled={!readOnly && PRAY_MODE_ENABLED}
            busy={highlightBusy}
            mutationError={
              highlightMutationFailed ? t('bible.versePanelSaveFailed') : null
            }
            focusOnOpen={versePanelFocusOnOpen}
            returnFocusRef={versePanelReturnFocusRef}
            onApply={handleApplyHighlight}
            onRemove={handleRemoveHighlight}
            onWriteNote={handlePanelWriteNote}
            onPray={handlePanelPray}
            onShare={handlePanelShare}
            onClose={() => setVersePanelOpen(false)}
            onCancel={() => {
              handleClearSelection();
              setAnchorVerse(null);
              setVersePanelOpen(false);
            }}
            zIndex={zVersePanel}
          />
        </>
      )}
      {/* v2 Share confirm toast (§4.6), shown only on the clipboard fallback.
          Reader-token-scoped, bottom-anchored, aria-live polite, auto-dismiss. */}
      {shareToast && (
        <div className={styles.shareToast} role="status" aria-live="polite">
          {t('bible.verseShareCopied')}
        </div>
      )}
      {journalToast && (
        <div
          className={`${styles.journalToast}${versePanelOpen ? ` ${styles.journalToastAbovePanel}` : ''}`}
          role="status"
          aria-live="polite"
          onMouseEnter={cancelJournalToast}
          onMouseLeave={scheduleJournalToast}
          onFocus={cancelJournalToast}
          onBlur={scheduleJournalToast}
        >
          <span className={styles.journalToastMessage}>
            {t('bookmark.journalSavedToast')}
          </span>
          <button
            type="button"
            className={styles.journalToastAction}
            onClick={() => {
              cancelJournalToast();
              setJournalToast(false);
              if (bookSlug) {
                // Unwind verse-panel (and any other reader layer) first.
                // A raw navigate while a NavStack layer is open races the
                // unmount cleanup's requestClose, which history.go(-n)s
                // back to the chapter.
                nav.resetToBaseThenNavigate(
                  `/bible/bookmarks/book/${bookSlug}/${chapterNum}`,
                );
              }
            }}
          >
            {t('bookmark.journalSavedView')}
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
